import { HubViewportCard } from "@/components/layout/HubViewportCard";
import { hubViewportTableCardClassName } from "@/lib/hubLayoutClassNames";
/**
 * Manufacturing management → Transfers workbench.
 *
 * Hub-side workbench covering the full transfer-execution lifecycle:
 *   - planner-side `mrp.transfer_request` -> `public.transfer_order (pending)` conversion
 *   - lifecycle transitions (pending → packing → ready_to_ship → shipped → delivered → partially_received → received),
 *     including packing → pending when Start packing was pressed by mistake
 *     and Cancel from any non-terminal status
 *   - simplified packing (boxes + per-line allocations) for external inbound transfers
 *   - receive variance capture
 *   - ad-hoc transfer creation (internal site-to-site and external inbound)
 *
 * UPS label and pickup capture remain on `/shipping/outbound`; SGS-origin ship uses
 * POST `/api/shipping/transfers/:id/mark-sent` from the workbench and outbound queue.
 *
 * Read source: GET /api/manufacturing/transfers/board.
 * Lifecycle writes go through the new `/api/manufacturing/transfers/*` family.
 * Convert/release/edit-pending lines stay under `/api/mrp/transfer-orders` and
 * `/api/mrp/transfer-requests` since they are planner-side actions.
 * Move a pending line to another pending transfer (or a new one) via
 * POST `/api/mrp/transfer-orders/:id/lines/:lineId/move`.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type DragEvent as ReactDragEvent,
} from "react";
import {
  useSearchParams,
} from "react-router-dom";
import {
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Download,
  ExternalLink,
  Printer,
} from "lucide-react";
import {
  apiFetch,
  ApiError,
} from "@/lib/api";
import { patchMrpForecastTransferRequestInCache } from "@/lib/mrpForecastQueryCache";
import {
  AssemblySkuCombobox,
} from "@/components/mrp/AssemblySkuCombobox";
import {
  MrpSkuWithUnitLabel,
} from "@/components/mrp/MrpSkuWithUnitLabel";
import {
  TransferReasonSelect,
  transferReasonDefault,
  useTransferReasonCatalog,
} from "@/components/mrp/TransferReasonSelect";
import {
  Badge,
} from "@/components/ui/Badge";
import {
  Button,
} from "@/components/ui/Button";
import {
  SaveChangesButton,
} from "@/components/ui/SaveChangesButton";
import {
  Card,
  CardBody,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import {
  useHubToast,
} from "@/components/ui/HubToast";
import {
  Input,
  Label,
  TextArea,
} from "@/components/ui/Input";
import {
  hubModalOverlayProps,
} from "@/components/ui/HubModalOverlay";
import {
  HoverHintContent,
  HoverHintTrigger,
  HoverHintWithGlyph,
  PageTitleWithHelp,
  ReadOnlyHoverLayer,
} from "@/components/ui/title-help";
import {
  ACTIVE_MRP_TRANSFER_REASONS,
  MRP_TRANSFER_REQUEST_CANCEL_REMAINDER_HOVER,
  MRP_TRANSFER_REQUEST_CANCEL_BUTTON_CLASS,
  mrpTransferReasonLabel,
  mrpTransferRequestCancelButtonLabel,
  mrpTransferRequestCancelConfirmMessage,
  normalizeMrpTransferReason,
  type MrpSkuHoverHintFields,
  type MrpTransferReason,
} from "@/lib/mrp";
import {
  useHubModuleWrite,
} from "@/lib/useHubModuleWrite";
import {
  cn,
} from "@/lib/utils";
import {
  TransferOrderDetailPanel,
} from "./TransferOrderDetailPanel";
import {
  TransferPackLaborInline,
  type TransferPackLaborSummary,
} from "./TransferPackLaborCard";
import {
  TransferLinePickPackHoverHint,
  TransferShipmentEventsInline,
} from "./TransferShipmentEventsInline";
import {
  TransferPackingQuantityApprovalsModal,
} from "./TransferPackingQuantityApprovalsModal";
import { TransferCommercialInvoiceModal } from "./TransferCommercialInvoiceModal";
import { isInternationalShipTo } from "@/pages/shipping/internationalShipTo";
import {
  TransferOriginOnHandCell,
  TransferOriginOnHandHeader,
} from "./TransferOriginOnHandCells";
import { TransferRequestTargetDateCell } from "./TransferRequestTargetDateCell";
import {
  buildTransferOriginOnHandLookup,
  mrpSiteCodeForLocationId,
  transferOrderHasOriginInventoryShortfall,
  useTransferOriginInventory,
} from "./transfersOriginInventory";
import {
  buildTransferLocationLabel,
  TransferLocationFilterButtons,
  TransferLocationRoute,
} from "./transfersWorkbenchUi";
import {
  transferOrderStatusBadgeTone,
  TRANSFER_ORDER_STATUS_LABELS,
  TRANSFER_ORDER_STATUS_VALUES,
  TRANSFER_ORDER_TERMINAL_STATUSES,
  type TransferBoardLocation,
  type TransferBoardOrder,
  type TransferBoardRequest,
  type TransferBoardResponse,
  type TransferOrderStatus,
  transferOrderPrePackHref,
  transferOrderShowsPrePackLink,
  transferOrderShowsOutboundLink,
  transferOrderShowsEditTracking,
  transferOrderShowsTransferOrderPdfLink,
  transferOrderIsSgsOrigin,
  primaryNextActionForTransferOrder,
  countPendingTransferPackingQuantityRequests,
  TRANSFER_INVENTORY_SHORTFALL_CHIP_LABEL,
  TRANSFER_INVENTORY_SHORTFALL_HOVER,
  TRANSFER_PENDING_QUANTITY_CHANGE_HOVER,
  transferOrderHasSealedCartons,
  transferOrderPackedQuantityByItemId,
  transferLineReceivedQuantityClassName,
  effectiveTransferPackQuantity,
  transferPendingQuantityChangeChipLabel,
} from "./transfersWorkbenchTypes";
import {
  downloadTransferOrderPdf,
  generateTransferOrderPdf,
} from "./transfersWorkbenchApi";
import { markTransferSent } from "@/pages/shipping/shippingHubApi";
import {
  compareTransferBoardOrders,
  DRAG_MIME,
  EMPTY_NEW_TRANSFER_DRAFT,
  FILTER_SELECT_CLASS,
  PAGE_HOVER_HINT,
  RECEIVE_DIALOG_HOVER_HINT,
  REQUEST_TARGET_DATE_OVERDUE,
  TRANSFER_CARD_STACKED_BUTTON_CLASS,
  TRANSFER_CARD_STACKED_LINK_CLASS,
  TRANSFER_ORDER_SORT_OPTIONS,
  TransferRequestReasonDetail,
  TransferSkuHoverCell,
  filterTogglePillClass,
  formatTransferRequestTargetMonthLabel,
  isRequestTargetDateOverdue,
  laneKeyForOrder,
  laneKeyForRequest,
  listPendingTransferMoveTargets,
  parseReceiveDraftQty,
  revertToPendingActionFor,
  stopTransferCardButtonClick,
  stopTransferCardLinkClick,
  targetDateToMonthKey,
  todayIsoYyyyMmDd,
  transferRequestSearchHaystack,
  toggleSingleSelectFilter,
  transferCardActionContent,
  transferCardActionKeyForPrimaryLabel,
  transferReceiveDraftRemaining,
  type TransferOrderSortKey,
} from "./transfersWorkbenchBoardChrome";
import type {
  ConvertDialogState,
  EditPendingOrderDraft,
  MovePendingLineDialogState,
  NewTransferDraft,
  ReceiveDialogState,
  ReceiveDraftLine,
} from "./transfersWorkbenchBoardChrome";

export function TransfersWorkbenchPage(): JSX.Element {
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const hubWritesAllowed = useHubModuleWrite("manufacturing_management");
  const { showToast } = useHubToast();

  const transferReasonCatalogQuery = useTransferReasonCatalog();
  const transferReasonCatalog = transferReasonCatalogQuery.data;

  const reasonFilterOptions = useMemo(() => {
    if (transferReasonCatalog != null && transferReasonCatalog.length > 0) {
      return transferReasonCatalog.map((entry) => entry.code);
    }
    return [...ACTIVE_MRP_TRANSFER_REASONS];
  }, [transferReasonCatalog]);

  const reasonLabel = useCallback(
    (reason: string) => mrpTransferReasonLabel(reason, transferReasonCatalog),
    [transferReasonCatalog],
  );
  const [originFilter, setOriginFilter] = useState<Set<number>>(() => new Set());
  const [destinationFilter, setDestinationFilter] = useState<Set<number>>(() => new Set());
  const [orderSearch, setOrderSearch] = useState("");
  const [requestSearch, setRequestSearch] = useState("");
  const [orderStatusFilter, setOrderStatusFilter] = useState<TransferOrderStatus | null>(null);
  const [orderOriginFilter, setOrderOriginFilter] = useState<number | null>(null);
  const [orderDestinationFilter, setOrderDestinationFilter] = useState<number | null>(null);
  const [orderSortKey, setOrderSortKey] = useState<TransferOrderSortKey>("status");

  const [openTransferRequestsExpanded, setOpenTransferRequestsExpanded] = useState(true);
  const [expandedOrderIds, setExpandedOrderIds] = useState<Set<number>>(() => new Set());
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);
  const [packQtyApprovalsOrderId, setPackQtyApprovalsOrderId] = useState<number | null>(null);
  const [commercialInvoiceOrderId, setCommercialInvoiceOrderId] = useState<number | null>(null);

  useEffect(() => {
    const searchRaw = searchParams.get("search")?.trim() ?? "";
    if (searchRaw !== "") {
      setOrderSearch(searchRaw);
      setRequestSearch(searchRaw);
      setOpenTransferRequestsExpanded(true);
    }
    const transferOrderIdRaw = searchParams.get("transfer_order_id")?.trim() ?? "";
    if (transferOrderIdRaw === "") return;
    const transferOrderId = Number.parseInt(transferOrderIdRaw, 10);
    if (!Number.isFinite(transferOrderId) || transferOrderId <= 0) return;
    setSelectedOrderId(transferOrderId);
    setExpandedOrderIds((current) => new Set(current).add(transferOrderId));
  }, [searchParams]);

  const toggleOrderExpanded = useCallback((orderId: number) => {
    setExpandedOrderIds((current) => {
      const next = new Set(current);
      if (next.has(orderId)) {
        next.delete(orderId);
      } else {
        next.add(orderId);
      }
      return next;
    });
  }, []);
  const [requestReasonFilter, setRequestReasonFilter] = useState<Set<MrpTransferReason>>(() => new Set());
  const [requestTargetMonthFilter, setRequestTargetMonthFilter] = useState("");
  const [selectedRequestIds, setSelectedRequestIds] = useState<Set<string>>(() => new Set());
  const [editingRequestTargetDateId, setEditingRequestTargetDateId] = useState<string | null>(null);
  const [convertDialog, setConvertDialog] = useState<ConvertDialogState | null>(null);
  const [createTransferDialogOpen, setCreateTransferDialogOpen] = useState(false);
  const [editPendingDialog, setEditPendingDialog] = useState<EditPendingOrderDraft | null>(null);
  const editPendingBaselineRef = useRef<string | null>(null);
  const [moveLineDialog, setMoveLineDialog] = useState<MovePendingLineDialogState | null>(null);
  const [newTransferDraft, setNewTransferDraft] = useState<NewTransferDraft>(
    EMPTY_NEW_TRANSFER_DRAFT,
  );
  const [receiveDialog, setReceiveDialog] = useState<ReceiveDialogState | null>(null);
  const [cancelTransferConfirm, setCancelTransferConfirm] = useState<{
    orderId: number;
    transferNumber: string | null;
  } | null>(null);
  const dragOverlayRef = useRef<{ orderId: number | "new" | null }>({ orderId: null });
  const [dragOverTarget, setDragOverTarget] = useState<number | "new" | null>(null);
  const [requestDragActive, setRequestDragActive] = useState(false);

  useEffect(() => {
    dragOverlayRef.current.orderId = dragOverTarget;
  }, [dragOverTarget]);

  const allTransferOrderStatusesParam = useMemo(() => {
    return TRANSFER_ORDER_STATUS_VALUES.join(",");
  }, []);

  const boardQuery = useQuery<TransferBoardResponse, ApiError>({
    queryKey: ["manufacturing", "transfers-board"],
    queryFn: ({ signal }) =>
      apiFetch<TransferBoardResponse>("/api/manufacturing/transfers/board", {
        query: {
          statuses: allTransferOrderStatusesParam,
        },
        signal,
      }),
    refetchOnWindowFocus: false,
  });
  const boardTransferOrderIds = useMemo(
    () => (boardQuery.data?.orders ?? []).map((order) => order.id).sort((left, right) => left - right),
    [boardQuery.data?.orders],
  );
  const boardPackLaborQuery = useQuery<
    { laborByTransferOrderId: Record<string, TransferPackLaborSummary> },
    ApiError
  >({
    queryKey: ["manufacturing", "transfer-pack-labor-batch", boardTransferOrderIds.join(",")],
    enabled: boardTransferOrderIds.length > 0,
    staleTime: 30_000,
    queryFn: ({ signal }) =>
      apiFetch<{ laborByTransferOrderId: Record<string, TransferPackLaborSummary> }>(
        "/api/manufacturing/transfers/pack-labor",
        {
          query: { ids: boardTransferOrderIds.join(",") },
          signal,
        },
      ),
  });

  // Deep link from the receive page's "Feeds T-xxxx" chip: ?expand=<order id>
  // expands that card and scrolls it into view once the board has loaded.
  // (`searchParams` is declared once at the top of the component.)
  const expandDeepLinkHandledRef = useRef<string | null>(null);
  useEffect(() => {
    const raw = searchParams.get("expand");
    if (raw == null || expandDeepLinkHandledRef.current === raw) return;
    if (boardQuery.data == null) return;
    const orderId = Number.parseInt(raw, 10);
    if (!Number.isFinite(orderId)) return;
    expandDeepLinkHandledRef.current = raw;
    setExpandedOrderIds((current) => {
      if (current.has(orderId)) return current;
      const next = new Set(current);
      next.add(orderId);
      return next;
    });
    requestAnimationFrame(() => {
      document.getElementById(`transfer-order-${orderId}`)?.scrollIntoView({ block: "center" });
    });
  }, [searchParams, boardQuery.data]);

  const locationsById = useMemo(() => {
    const map = new Map<number, TransferBoardLocation>();
    for (const location of boardQuery.data?.locations ?? []) {
      map.set(location.id, location);
    }
    return map;
  }, [boardQuery.data?.locations]);

  const moveLineTargetOrders = useMemo(() => {
    if (moveLineDialog == null) return [];
    return listPendingTransferMoveTargets(
      boardQuery.data?.orders ?? [],
      moveLineDialog.orderId,
      moveLineDialog.origin_location_id,
      moveLineDialog.destination_location_id,
    );
  }, [boardQuery.data?.orders, moveLineDialog]);

  const normalizedOrderSearch = orderSearch.trim().toLowerCase();
  const normalizedRequestSearch = requestSearch.trim().toLowerCase();

  const requestTargetMonthOptions = useMemo(() => {
    const months = new Set<string>();
    for (const row of boardQuery.data?.requests ?? []) {
      const monthKey = targetDateToMonthKey(row.target_date);
      if (monthKey != null) months.add(monthKey);
    }
    return [...months].sort((left, right) => left.localeCompare(right));
  }, [boardQuery.data?.requests]);

  const boardCatalogSkus = useMemo(() => {
    const skus = new Set<string>();
    for (const row of boardQuery.data?.requests ?? []) {
      const trimmed = row.sku_id.trim();
      if (trimmed !== "") skus.add(trimmed);
    }
    for (const order of boardQuery.data?.orders ?? []) {
      for (const line of order.lines) {
        const trimmed = (line.sku_id ?? "").trim();
        if (trimmed !== "") skus.add(trimmed);
      }
    }
    for (const line of newTransferDraft.lines) {
      const trimmed = line.sku.trim();
      if (trimmed !== "") skus.add(trimmed);
    }
    if (editPendingDialog != null) {
      for (const line of editPendingDialog.lines) {
        const trimmed = line.sku.trim();
        if (trimmed !== "") skus.add(trimmed);
      }
    }
    return [...skus].sort();
  }, [
    boardQuery.data?.requests,
    boardQuery.data?.orders,
    editPendingDialog,
    newTransferDraft.lines,
  ]);

  const [boardSkuHintsFetchEnabled, setBoardSkuHintsFetchEnabled] = useState(false);
  const enableBoardSkuHintsFetch = useCallback(() => {
    setBoardSkuHintsFetchEnabled(true);
  }, []);

  useEffect(() => {
    if (boardCatalogSkus.length === 0) {
      return;
    }
    const idleId =
      typeof requestIdleCallback === "function"
        ? requestIdleCallback(() => setBoardSkuHintsFetchEnabled(true))
        : window.setTimeout(() => setBoardSkuHintsFetchEnabled(true), 0);
    return () => {
      if (typeof cancelIdleCallback === "function" && typeof idleId === "number") {
        cancelIdleCallback(idleId);
      } else {
        window.clearTimeout(idleId as number);
      }
    };
  }, [boardCatalogSkus]);

  useEffect(() => {
    if (normalizedRequestSearch !== "") {
      enableBoardSkuHintsFetch();
    }
  }, [enableBoardSkuHintsFetch, normalizedRequestSearch]);

  const boardSkuHintsQuery = useQuery<
    { sku_hover_hints: Record<string, MrpSkuHoverHintFields> },
    ApiError
  >({
    queryKey: ["mrp", "sku-hover-hints", "transfers-board", boardCatalogSkus.join("\n")],
    queryFn: ({ signal }) =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: boardCatalogSkus.join(",") },
        signal,
        priority: "speculative",
      }),
    enabled: boardSkuHintsFetchEnabled && boardCatalogSkus.length > 0,
    staleTime: 60_000,
  });

  const boardSkuHoverHints = boardSkuHintsQuery.data?.sku_hover_hints;
  const boardSkuHintsLoading =
    boardSkuHintsFetchEnabled && (boardSkuHintsQuery.isPending || boardSkuHintsQuery.isFetching);

  const filteredRequests = useMemo(() => {
    let rows = boardQuery.data?.requests ?? [];
    if (originFilter.size > 0) {
      rows = rows.filter((row) => originFilter.has(row.origin_location_id));
    }
    if (destinationFilter.size > 0) {
      rows = rows.filter((row) => destinationFilter.has(row.destination_location_id));
    }
    if (requestReasonFilter.size > 0) {
      rows = rows.filter((row) => requestReasonFilter.has(row.reason as MrpTransferReason));
    }
    if (requestTargetMonthFilter !== "") {
      rows = rows.filter((row) => {
        if (requestTargetMonthFilter === REQUEST_TARGET_DATE_OVERDUE) {
          return isRequestTargetDateOverdue(row.target_date);
        }
        return targetDateToMonthKey(row.target_date) === requestTargetMonthFilter;
      });
    }
    if (normalizedRequestSearch !== "") {
      rows = rows.filter((row) => {
        const hint = boardSkuHoverHints?.[row.sku_id.trim()];
        const hay = transferRequestSearchHaystack(row, reasonLabel, hint);
        return hay.includes(normalizedRequestSearch);
      });
    }
    return rows;
  }, [
    boardQuery.data?.requests,
    boardSkuHoverHints,
    destinationFilter,
    normalizedRequestSearch,
    originFilter,
    reasonLabel,
    requestReasonFilter,
    requestTargetMonthFilter,
  ]);

  const filteredOrders = useMemo(() => {
    let rows = boardQuery.data?.orders ?? [];
    if (orderStatusFilter != null) {
      rows = rows.filter((row) => row.status === orderStatusFilter);
    }
    if (orderOriginFilter != null) {
      rows = rows.filter((row) => row.origin_location_id === orderOriginFilter);
    }
    if (orderDestinationFilter != null) {
      rows = rows.filter((row) => row.destination_location_id === orderDestinationFilter);
    }
    if (normalizedOrderSearch !== "") {
      rows = rows.filter((row) => {
        const hay = [
          row.transfer_number ?? "",
          row.notes ?? "",
          row.ship_by_date ?? "",
          buildTransferLocationLabel(locationsById.get(row.origin_location_id), row.origin_location_id),
          buildTransferLocationLabel(locationsById.get(row.destination_location_id), row.destination_location_id),
          ...row.lines.map((line) => line.sku_id ?? ""),
          ...row.lines.map((line) => line.reason ?? ""),
        ]
          .join(" ")
          .toLowerCase();
        return hay.includes(normalizedOrderSearch);
      });
    }
    return [...rows].sort((left, right) => compareTransferBoardOrders(left, right, orderSortKey));
  }, [
    boardQuery.data?.orders,
    locationsById,
    normalizedOrderSearch,
    orderDestinationFilter,
    orderOriginFilter,
    orderSortKey,
    orderStatusFilter,
  ]);

  const selectedRequests = useMemo(
    () => (boardQuery.data?.requests ?? []).filter((row) => selectedRequestIds.has(row.id)),
    [boardQuery.data?.requests, selectedRequestIds],
  );

  const sharedLane = useMemo(() => {
    if (selectedRequests.length === 0) return null;
    const first = laneKeyForRequest(selectedRequests[0]!);
    if (selectedRequests.every((row) => laneKeyForRequest(row) === first)) {
      return {
        key: first,
        originId: selectedRequests[0]!.origin_location_id,
        destinationId: selectedRequests[0]!.destination_location_id,
      };
    }
    return null;
  }, [selectedRequests]);


  const packQtyApprovalsOrder = useMemo(() => {
    if (packQtyApprovalsOrderId == null) return null;
    return filteredOrders.find((row) => row.id === packQtyApprovalsOrderId) ?? null;
  }, [filteredOrders, packQtyApprovalsOrderId]);

  const selectedOrder = useMemo(
    () => filteredOrders.find((row) => row.id === selectedOrderId) ?? null,
    [filteredOrders, selectedOrderId],
  );

  useEffect(() => {
    if (packQtyApprovalsOrderId == null) return;
    if (
      packQtyApprovalsOrder == null
      || countPendingTransferPackingQuantityRequests(packQtyApprovalsOrder) === 0
    ) {
      setPackQtyApprovalsOrderId(null);
    }
  }, [packQtyApprovalsOrder, packQtyApprovalsOrderId]);

  const originSiteCodesForInventory = useMemo(() => {
    const codes = new Set<string>();
    const addLocationId = (locationId: number) => {
      const siteCode = mrpSiteCodeForLocationId(locationsById, locationId);
      if (siteCode != null) codes.add(siteCode);
    };
    for (const row of filteredRequests) addLocationId(row.origin_location_id);
    for (const order of filteredOrders) addLocationId(order.origin_location_id);
    if (newTransferDraft.origin_location_id.trim() !== "") {
      const parsed = Number.parseInt(newTransferDraft.origin_location_id, 10);
      if (Number.isFinite(parsed)) addLocationId(parsed);
    }
    if (editPendingDialog != null) addLocationId(editPendingDialog.origin_location_id);
    if (convertDialog != null) {
      for (const requestId of convertDialog.requestIds) {
        const requestRow = (boardQuery.data?.requests ?? []).find((row) => row.id === requestId);
        if (requestRow != null) addLocationId(requestRow.origin_location_id);
      }
    }
    return [...codes];
  }, [
    boardQuery.data?.requests,
    convertDialog,
    editPendingDialog,
    filteredOrders,
    filteredRequests,
    locationsById,
    newTransferDraft.origin_location_id,
  ]);

  const { inventoryBySite, isLoading: originInventoryLoading } = useTransferOriginInventory(
    originSiteCodesForInventory,
    boardCatalogSkus,
  );

  const lookupOriginOnHand = useMemo(
    () => buildTransferOriginOnHandLookup(locationsById, inventoryBySite),
    [inventoryBySite, locationsById],
  );

  const editPendingLineSkus = useMemo(
    () =>
      editPendingDialog == null
        ? []
        : [...new Set(editPendingDialog.lines.map((line) => line.sku.trim()).filter(Boolean))],
    [editPendingDialog],
  );

  const editPendingSkuHintsQuery = useQuery<
    { sku_hover_hints: Record<string, MrpSkuHoverHintFields> },
    ApiError
  >({
    queryKey: ["mrp", "sku-hover-hints", "transfer-edit-pending", editPendingLineSkus.join(",")],
    enabled: editPendingLineSkus.length > 0,
    queryFn: ({ signal }) =>
      apiFetch<{ sku_hover_hints: Record<string, MrpSkuHoverHintFields> }>("/api/mrp/sku-hover-hints", {
        query: { cpns: editPendingLineSkus.join(",") },
        signal,
        priority: "speculative",
      }),
  });

  const lookupEditPendingPackSize = useCallback(
    (sku: string): number | null => {
      const trimmed = sku.trim();
      if (trimmed === "") return null;
      const orderMultiple = editPendingSkuHintsQuery.data?.sku_hover_hints[trimmed]?.order_multiple;
      return orderMultiple != null && orderMultiple >= 1 ? orderMultiple : null;
    },
    [editPendingSkuHintsQuery.data?.sku_hover_hints],
  );

  const invalidateBoard = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["manufacturing", "transfers-board"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "incoming"] });
    void queryClient.invalidateQueries({ queryKey: ["mrp", "transfer-requests"] });
  }, [queryClient]);

  const updateRequestTargetDateMutation = useMutation<
    unknown,
    ApiError,
    { id: string; target_date: string }
  >({
    mutationFn: ({ id, target_date }) =>
      apiFetch(`/api/mrp/transfer-requests/${id}`, {
        method: "PATCH",
        body: { target_date },
      }),
    onSuccess: () => {
      setEditingRequestTargetDateId(null);
      invalidateBoard();
    },
  });

  // ---------- planner-side convert (existing /api/mrp endpoints) ----------
  const convertMutation = useMutation<
    { id: number; transfer_number: string | null },
    ApiError,
    {
      requestIds: string[];
      transferOrderId: number | null;
      shipByDate: string | null;
      quantityOverrides: { transfer_request_id: number; quantity: number }[];
    }
  >({
    mutationFn: (args) =>
      apiFetch<{ id: number; transfer_number: string | null }>(
        "/api/mrp/transfer-orders/from-requests",
        {
          method: "POST",
          body: {
            request_ids: args.requestIds.map((id) => Number.parseInt(id, 10)),
            transfer_order_id: args.transferOrderId ?? undefined,
            ship_by_date: args.shipByDate ?? undefined,
            request_quantity_overrides: args.quantityOverrides,
          },
        },
      ),
    onSuccess: (result) => {
      setConvertDialog(null);
      setSelectedRequestIds(new Set());
      setExpandedOrderIds((current) => new Set(current).add(result.id));
      setSelectedOrderId(result.id);
      invalidateBoard();
      showToast({
        tone: "success",
        message: `Pending transfer order ${result.transfer_number ?? `#${String(result.id)}`} updated.`,
      });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  // ---------- ad-hoc transfer create (/api/manufacturing/transfers) ----------
  const createTransferMutation = useMutation<
    { transfer_order: { id: number; transfer_number: string | null; status: TransferOrderStatus } },
    ApiError,
    NewTransferDraft
  >({
    mutationFn: (draft) =>
      apiFetch<{ transfer_order: { id: number; transfer_number: string | null; status: TransferOrderStatus } }>(
        "/api/manufacturing/transfers",
        {
          method: "POST",
          body: {
            origin_location_id: Number.parseInt(draft.origin_location_id, 10),
            destination_location_id: Number.parseInt(draft.destination_location_id, 10),
            ship_by_date: draft.ship_by_date,
            default_line_reason: draft.default_line_reason,
            notes: draft.notes.trim() || null,
            lines: draft.lines
              .filter((line) => line.sku.trim() !== "" && line.quantity.trim() !== "")
              .map((line) => ({
                sku: line.sku.trim(),
                quantity: Number.parseInt(line.quantity, 10),
                reason: line.reason,
              })),
          },
        },
      ),
    onSuccess: ({ transfer_order }) => {
      setCreateTransferDialogOpen(false);
      setNewTransferDraft(EMPTY_NEW_TRANSFER_DRAFT);
      setExpandedOrderIds((current) => new Set(current).add(transfer_order.id));
      setSelectedOrderId(transfer_order.id);
      invalidateBoard();
      showToast({
        tone: "success",
        message: `Transfer ${transfer_order.transfer_number ?? `#${String(transfer_order.id)}`} created.`,
      });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  // ---------- lifecycle transition ----------
  const transitionMutation = useMutation<
    { transfer_order: { id: number; status: TransferOrderStatus } },
    ApiError,
    { orderId: number; to: TransferOrderStatus; notes?: string | null }
  >({
    mutationFn: ({ orderId, to, notes }) =>
      apiFetch<{ transfer_order: { id: number; status: TransferOrderStatus } }>(
        `/api/manufacturing/transfers/${String(orderId)}/transition`,
        { method: "POST", body: { to, notes: notes ?? undefined } },
      ),
    onSuccess: ({ transfer_order }) => {
      invalidateBoard();
      showToast({
        tone: "success",
        message: `Transfer order moved to ${TRANSFER_ORDER_STATUS_LABELS[transfer_order.status]}.`,
      });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const shipTransferMutation = useMutation<void, ApiError, { orderId: number }>({
    mutationFn: ({ orderId }) =>
      markTransferSent(orderId, null, null, { requireReadyForPickup: false }),
    onSuccess: () => {
      invalidateBoard();
      showToast({ tone: "success", message: "Transfer order marked shipped." });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const transferOrderPdfMutation = useMutation<
    string,
    ApiError,
    { orderId: number; transferNumber: string | null }
  >({
    mutationFn: ({ orderId }) => generateTransferOrderPdf(orderId),
    onSuccess: (pdfBase64, { orderId, transferNumber }) => {
      downloadTransferOrderPdf(pdfBase64, transferNumber, orderId);
      showToast({ tone: "success", message: "Transfer order PDF downloaded." });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const outboundTrackingMutation = useMutation<
    { transfer_order_id: number; outbound_carrier: string | null; outbound_tracking: string | null },
    ApiError,
    { orderId: number; carrier: string | null; tracking: string | null }
  >({
    mutationFn: ({ orderId, carrier, tracking }) =>
      apiFetch<{
        transfer_order_id: number;
        outbound_carrier: string | null;
        outbound_tracking: string | null;
      }>(`/api/manufacturing/transfers/${String(orderId)}/outbound-tracking`, {
        method: "PATCH",
        body: { carrier, tracking },
      }),
    onSuccess: () => {
      invalidateBoard();
      showToast({ tone: "success", message: "Transfer tracking updated." });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  // ---------- receive ----------
  const receiveMutation = useMutation<
    {
      transfer_order: { id: number; status: TransferOrderStatus };
      follow_up_transfer_order: { id: number; transfer_number: string; status: string } | null;
    },
    ApiError,
    { orderId: number; lines: ReceiveDraftLine[]; notes: string; createFollowUpTransfer: boolean }
  >({
    mutationFn: ({ orderId, lines, notes, createFollowUpTransfer }) =>
      apiFetch<{
        transfer_order: { id: number; status: TransferOrderStatus };
        follow_up_transfer_order: { id: number; transfer_number: string; status: string } | null;
      }>(
        `/api/manufacturing/transfers/${String(orderId)}/receive`,
        {
          method: "POST",
          body: {
            notes: notes.trim() === "" ? null : notes.trim(),
            create_follow_up_transfer: createFollowUpTransfer,
            lines: lines
              .filter((line) => {
                const receivedQty = parseReceiveDraftQty(line.received_qty);
                const returnQty = parseReceiveDraftQty(line.return_to_origin_qty);
                const lostQty = parseReceiveDraftQty(line.lost_qty);
                return (
                  (Number.isInteger(receivedQty) && receivedQty > 0)
                  || (Number.isInteger(returnQty) && returnQty > 0)
                  || (Number.isInteger(lostQty) && lostQty > 0)
                );
              })
              .map((line) => ({
                transfer_order_item_id: line.transfer_order_item_id,
                received_qty: parseReceiveDraftQty(line.received_qty) || 0,
                return_to_origin_qty: parseReceiveDraftQty(line.return_to_origin_qty) || 0,
                lost_qty: parseReceiveDraftQty(line.lost_qty) || 0,
                notes: line.notes.trim() === "" ? null : line.notes.trim(),
              })),
          },
        },
      ),
    onSuccess: ({ transfer_order, follow_up_transfer_order }) => {
      setReceiveDialog(null);
      invalidateBoard();
      const followUp =
        follow_up_transfer_order != null
          ? ` Created follow-up ${follow_up_transfer_order.transfer_number}.`
          : "";
      showToast({
        tone: "success",
        message: `Receive submitted. Status: ${TRANSFER_ORDER_STATUS_LABELS[transfer_order.status]}.${followUp}`,
      });
    },
  });

  const cancelRequestMutation = useMutation<
    { cancelled: boolean },
    ApiError,
    { id: string; remainder: boolean }
  >({
    mutationFn: ({ id }) =>
      apiFetch<{ cancelled: boolean }>(`/api/mrp/transfer-requests/${id}`, { method: "DELETE" }),
    onSuccess: (_data, { id, remainder }) => {
      const row = (boardQuery.data?.requests ?? []).find((entry) => entry.id === id);
      if (row != null && row.remaining_quantity > 0) {
        const patched = patchMrpForecastTransferRequestInCache(queryClient, {
          cpn: row.sku_id,
          originLocationCode: mrpSiteCodeForLocationId(locationsById, row.origin_location_id),
          destinationLocationCode: mrpSiteCodeForLocationId(locationsById, row.destination_location_id),
          targetDate: row.target_date,
          remainingQuantity: row.remaining_quantity,
          sign: -1,
        });
        if (!patched) {
          void queryClient.invalidateQueries({ queryKey: ["mrp", "forecast"] });
        }
      }
      invalidateBoard();
      showToast({
        tone: "success",
        message: remainder
          ? "Remaining request closed. Existing transfer orders were not changed."
          : "Transfer request cancelled.",
      });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const editPendingMutation = useMutation<
    { ok: boolean; pack_quantity_requests_created?: number },
    ApiError,
    EditPendingOrderDraft
  >({
    mutationFn: (draft) =>
      apiFetch<{ ok: boolean; pack_quantity_requests_created?: number }>(
        `/api/mrp/transfer-orders/${String(draft.orderId)}/pending-draft`,
        {
        method: "PATCH",
        body: {
          ship_by_date: draft.ship_by_date,
          default_line_reason: draft.default_line_reason,
          notes: draft.notes.trim() === "" ? null : draft.notes.trim(),
          lines: draft.lines.map((line) => ({
            id: line.id ?? undefined,
            sku: line.sku.trim(),
            quantity: Number.parseInt(line.quantity, 10),
            reason: line.reason,
          })),
        },
      }),
    onSuccess: (response, draft) => {
      setEditPendingDialog(null);
      invalidateBoard();
      const approvalCount = response.pack_quantity_requests_created ?? 0;
      showToast({
        tone: "success",
        message:
          approvalCount > 0
            ? `Pending transfer order ${draft.transferNumber ?? `#${String(draft.orderId)}`} updated. ${String(approvalCount)} pack quantity approval${approvalCount === 1 ? "" : "s"} submitted.`
            : `Pending transfer order ${draft.transferNumber ?? `#${String(draft.orderId)}`} updated.`,
      });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  const moveLineMutation = useMutation<
    {
      ok: boolean;
      source_transfer_order_id: number;
      source_deleted: boolean;
      target_transfer_order_id: number;
      target_transfer_number: string | null;
      moved_line_id: number;
    },
    ApiError,
    MovePendingLineDialogState
  >({
    mutationFn: (draft) => {
      const lineId = draft.line.id;
      if (lineId == null) {
        throw new ApiError(400, "Save the transfer before moving lines.");
      }
      const body: {
        target_transfer_order_id?: number | null;
        ship_by_date?: string;
        notes?: string | null;
      } = {};
      if (draft.targetMode === "existing") {
        const targetId = Number.parseInt(draft.target_transfer_order_id, 10);
        if (!Number.isFinite(targetId) || targetId <= 0) {
          throw new ApiError(400, "Choose a target pending transfer.");
        }
        body.target_transfer_order_id = targetId;
      } else {
        body.target_transfer_order_id = null;
        body.ship_by_date = draft.ship_by_date;
        body.notes = draft.notes.trim() === "" ? null : draft.notes.trim();
      }
      return apiFetch(
        `/api/mrp/transfer-orders/${String(draft.orderId)}/lines/${String(lineId)}/move`,
        { method: "POST", body },
      );
    },
    onSuccess: (result, draft) => {
      setMoveLineDialog(null);
      if (result.source_deleted) {
        setEditPendingDialog(null);
      } else {
        setEditPendingDialog((current) => {
          if (current == null || current.orderId !== result.source_transfer_order_id) {
            return current;
          }
          return {
            ...current,
            lines: current.lines.filter((line) => line.id !== result.moved_line_id),
          };
        });
      }
      setExpandedOrderIds((current) => new Set(current).add(result.target_transfer_order_id));
      if (result.source_deleted) {
        setSelectedOrderId(result.target_transfer_order_id);
      }
      invalidateBoard();
      const targetLabel = result.target_transfer_number ?? `#${String(result.target_transfer_order_id)}`;
      const skuLabel = draft.line.sku.trim() || "Line";
      showToast({
        tone: "success",
        message: result.source_deleted
          ? `${skuLabel} moved to ${targetLabel}. Source transfer was removed because it had no lines left.`
          : `${skuLabel} moved to ${targetLabel}.`,
      });
    },
    onError: (error) => showToast({ tone: "error", message: error.message }),
  });

  // ---------- helpers ----------
  const openConvertDialog = useCallback(
    (mode: "new-order" | "existing-order", transferOrderId: number | null, requestIds: string[]) => {
      const overrides: Record<string, string> = {};
      const requests = (boardQuery.data?.requests ?? []).filter((row) => requestIds.includes(row.id));
      for (const row of requests) {
        overrides[row.id] = String(Math.max(1, row.remaining_quantity));
      }
      const referenceRequest = requests[0];
      const referenceOrder =
        transferOrderId != null
          ? (boardQuery.data?.orders ?? []).find((row) => row.id === transferOrderId)
          : null;
      setConvertDialog({
        mode,
        transferOrderId,
        requestIds,
        shipByDate:
          referenceOrder?.ship_by_date ?? referenceRequest?.target_date ?? todayIsoYyyyMmDd(),
        quantityOverrides: overrides,
      });
    },
    [boardQuery.data?.orders, boardQuery.data?.requests],
  );

  const submitConvert = useCallback(() => {
    if (!convertDialog) return;
    const overrides: { transfer_request_id: number; quantity: number }[] = [];
    for (const id of convertDialog.requestIds) {
      const raw = convertDialog.quantityOverrides[id] ?? "";
      const parsed = Number.parseInt(raw, 10);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        showToast({ tone: "error", message: `Quantity for request #${id} must be a positive integer.` });
        return;
      }
      overrides.push({ transfer_request_id: Number.parseInt(id, 10), quantity: parsed });
    }
    convertMutation.mutate({
      requestIds: convertDialog.requestIds,
      transferOrderId: convertDialog.transferOrderId,
      shipByDate: convertDialog.mode === "new-order" ? convertDialog.shipByDate : null,
      quantityOverrides: overrides,
    });
  }, [convertDialog, convertMutation, showToast]);

  const openReceiveDialog = useCallback((order: TransferBoardOrder) => {
    setReceiveDialog({
      transferOrderId: order.id,
      transferNumber: order.transfer_number,
      notes: "",
      createFollowUpTransfer: false,
      lines: order.lines.map((line) => {
        const expectedQty = effectiveTransferPackQuantity(line);
        const alreadyReceived = line.received_quantity ?? 0;
        const alreadyReturn = line.return_to_origin_quantity ?? 0;
        const alreadyLost = line.lost_quantity ?? 0;
        const remaining = Math.max(0, expectedQty - alreadyReceived - alreadyReturn - alreadyLost);
        return {
          transfer_order_item_id: line.id,
          sku_id: line.sku_id,
          expected_qty: expectedQty,
          already_received_qty: alreadyReceived,
          already_return_qty: alreadyReturn,
          already_lost_qty: alreadyLost,
          received_qty: String(remaining),
          return_to_origin_qty: "0",
          lost_qty: "0",
          notes: "",
        };
      }),
    });
  }, []);

  const openEditPendingDialog = useCallback((order: TransferBoardOrder) => {
    const defaultLineReason = normalizeMrpTransferReason(
      order.lines[0]?.reason ?? transferReasonDefault(),
    );
    const packedByItemId = transferOrderPackedQuantityByItemId(order);
    const next: EditPendingOrderDraft = {
      orderId: order.id,
      origin_location_id: order.origin_location_id,
      destination_location_id: order.destination_location_id,
      transferNumber: order.transfer_number,
      ship_by_date: order.ship_by_date ?? todayIsoYyyyMmDd(),
      default_line_reason: defaultLineReason,
      notes: order.notes ?? "",
      pendingPackQtyRequestCount: countPendingTransferPackingQuantityRequests(order),
      lines: order.lines.map((line) => ({
        id: line.id,
        sku: line.sku_id ?? "",
        quantity: String(line.quantity),
        reason: normalizeMrpTransferReason(line.reason),
        lockedSku: line.fulfilled_transfer_request_id != null,
        allocatedQuantity: packedByItemId.get(line.id) ?? 0,
        sourceLabel:
          line.fulfilled_transfer_request_id != null
            ? `Request #${line.fulfilled_transfer_request_id}`
            : "Ad-hoc",
      })),
    };
    editPendingBaselineRef.current = JSON.stringify(next);
    setEditPendingDialog(next);
  }, []);

  const submitEditPending = useCallback(() => {
    if (!editPendingDialog) return;
    if (editPendingDialog.lines.length === 0) {
      showToast({ tone: "error", message: "At least one line is required." });
      return;
    }
    for (const line of editPendingDialog.lines) {
      if (line.sku.trim() === "") {
        showToast({ tone: "error", message: "Each line needs a SKU." });
        return;
      }
      const parsed = Number.parseInt(line.quantity, 10);
      if (!Number.isFinite(parsed) || parsed <= 0) {
        showToast({ tone: "error", message: `Quantity for ${line.sku.trim()} must be a positive integer.` });
        return;
      }
    }
    editPendingMutation.mutate(editPendingDialog);
  }, [editPendingDialog, editPendingMutation, showToast]);

  const editPendingDirty =
    editPendingDialog != null &&
    editPendingBaselineRef.current != null &&
    JSON.stringify(editPendingDialog) !== editPendingBaselineRef.current;

  const clearRequestDrag = useCallback((): void => {
    setRequestDragActive(false);
    setDragOverTarget(null);
  }, []);

  const handleRequestDragStart = useCallback(
    (event: ReactDragEvent<HTMLTableRowElement>, request: TransferBoardRequest) => {
      if (!hubWritesAllowed) {
        event.preventDefault();
        return;
      }
      setRequestDragActive(true);
      let dragRequestIds: string[];
      if (selectedRequestIds.has(request.id) && selectedRequestIds.size > 1) {
        dragRequestIds = Array.from(selectedRequestIds);
      } else {
        dragRequestIds = [request.id];
        setSelectedRequestIds(new Set([request.id]));
      }
      event.dataTransfer.effectAllowed = "move";
      event.dataTransfer.setData(DRAG_MIME, dragRequestIds.join(","));
      event.dataTransfer.setData("text/plain", dragRequestIds.join(","));
    },
    [hubWritesAllowed, selectedRequestIds],
  );

  const handleRequestDragEnd = useCallback((): void => {
    clearRequestDrag();
  }, [clearRequestDrag]);

  const handleDragOverTarget = useCallback(
    (event: ReactDragEvent<HTMLElement>, target: number | "new"): void => {
      if (!hubWritesAllowed) return;
      const ids = event.dataTransfer.types.includes(DRAG_MIME) ? "request" : null;
      if (ids == null) return;
      event.preventDefault();
      event.dataTransfer.dropEffect = "move";
      if (dragOverlayRef.current.orderId !== target) {
        setDragOverTarget(target);
      }
    },
    [hubWritesAllowed],
  );

  const handleDragLeaveTarget = useCallback((target: number | "new"): void => {
    if (dragOverlayRef.current.orderId === target) {
      setDragOverTarget(null);
    }
  }, []);

  const handleDropOnTarget = useCallback(
    (event: ReactDragEvent<HTMLElement>, target: number | "new"): void => {
      event.preventDefault();
      clearRequestDrag();
      if (!hubWritesAllowed) return;
      const payload = event.dataTransfer.getData(DRAG_MIME);
      if (payload === "") return;
      const ids = payload.split(",").map((entry) => entry.trim()).filter((entry) => entry !== "");
      if (ids.length === 0) return;

      const droppedRequests = (boardQuery.data?.requests ?? []).filter((row) => ids.includes(row.id));
      if (droppedRequests.length !== ids.length) {
        showToast({ tone: "error", message: "Could not find every dropped request in the current board." });
        return;
      }
      const lanes = new Set(droppedRequests.map(laneKeyForRequest));
      if (lanes.size > 1) {
        showToast({
          tone: "error",
          message: "All dropped requests must share origin and destination.",
        });
        return;
      }

      if (target === "new") {
        openConvertDialog("new-order", null, ids);
        return;
      }
      const order = (boardQuery.data?.orders ?? []).find((row) => row.id === target);
      if (order == null) {
        showToast({ tone: "error", message: "Target transfer order was not found." });
        return;
      }
      if (order.status !== "pending") {
        showToast({
          tone: "error",
          message: `Order is ${TRANSFER_ORDER_STATUS_LABELS[order.status]}; only pending orders can absorb requests.`,
        });
        return;
      }
      const droppedLaneKey = laneKeyForRequest(droppedRequests[0]!);
      if (laneKeyForOrder(order) !== droppedLaneKey) {
        showToast({
          tone: "error",
          message: `Lane mismatch: order is ${buildTransferLocationLabel(locationsById.get(order.origin_location_id), order.origin_location_id)} → ${buildTransferLocationLabel(locationsById.get(order.destination_location_id), order.destination_location_id)}.`,
        });
        return;
      }
      openConvertDialog("existing-order", order.id, ids);
    },
    [boardQuery.data?.orders, boardQuery.data?.requests, clearRequestDrag, hubWritesAllowed, locationsById, openConvertDialog, showToast],
  );

  const toggleRequestSelected = useCallback((id: string): void => {
    setSelectedRequestIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const selectOrderStatusFilter = useCallback((status: TransferOrderStatus): void => {
    setOrderStatusFilter((current) => toggleSingleSelectFilter(current, status));
  }, []);

  const selectOrderOriginFilter = useCallback((locationId: number): void => {
    setOrderOriginFilter((current) => toggleSingleSelectFilter(current, locationId));
  }, []);

  const selectOrderDestinationFilter = useCallback((locationId: number): void => {
    setOrderDestinationFilter((current) => toggleSingleSelectFilter(current, locationId));
  }, []);

  const toggleOriginFilter = useCallback((locationId: number): void => {
    setOriginFilter((current) => {
      const next = new Set(current);
      if (next.has(locationId)) next.delete(locationId); else next.add(locationId);
      return next;
    });
  }, []);

  const toggleDestinationFilter = useCallback((locationId: number): void => {
    setDestinationFilter((current) => {
      const next = new Set(current);
      if (next.has(locationId)) next.delete(locationId); else next.add(locationId);
      return next;
    });
  }, []);

  const toggleRequestReasonFilter = useCallback((reason: MrpTransferReason): void => {
    setRequestReasonFilter((current) => {
      const next = new Set(current);
      if (next.has(reason)) next.delete(reason); else next.add(reason);
      return next;
    });
  }, []);

  const requestCount = boardQuery.data?.requests.length ?? 0;
  const orderCount = boardQuery.data?.orders.length ?? 0;

  return (
    <div className="space-y-4">
      <header className="flex flex-col gap-2 sm:flex-row sm:items-baseline sm:justify-between">
        <PageTitleWithHelp hoverHint={PAGE_HOVER_HINT}>Transfers</PageTitleWithHelp>
        <p className="text-sm text-slate-700">
          <span className="font-semibold text-slate-900">Open requests:</span> {requestCount}
          <span className="ml-3 font-semibold text-slate-900">Transfer orders:</span> {orderCount}
        </p>
      </header>

      {boardQuery.isError ? (
        <Card>
          <CardBody>
            <p className="text-sm font-medium text-rose-800">Could not load the transfers board.</p>
            <p className="mt-1 text-sm text-rose-700">{boardQuery.error.message}</p>
          </CardBody>
        </Card>
      ) : null}

      <div
        className={cn(
          "grid min-h-[28rem] gap-4 lg:items-stretch",
          openTransferRequestsExpanded
            ? "lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]"
            : "lg:grid-cols-[auto_minmax(0,1fr)]",
        )}
      >
      <Card
        className={cn(
          hubViewportTableCardClassName("standard"),
          !openTransferRequestsExpanded && "lg:w-11 lg:overflow-hidden",
        )}
      >
        {!openTransferRequestsExpanded ? (
          <button
            type="button"
            aria-expanded={false}
            aria-controls="open-transfer-requests-panel"
            onClick={() => setOpenTransferRequestsExpanded(true)}
            className="hidden h-full min-h-[28rem] w-11 flex-1 flex-col items-center justify-center gap-2 bg-slate-50 px-1.5 text-slate-600 hover:bg-slate-100 hover:text-slate-900 lg:flex"
          >
            <ChevronRight className="h-4 w-4 shrink-0" aria-hidden />
            <span className="[writing-mode:vertical-rl] text-xs font-semibold text-slate-800">
              Open transfer requests ({filteredRequests.length})
            </span>
          </button>
        ) : null}
        <CardHeader
          className={cn(
            "shrink-0 space-y-3",
            !openTransferRequestsExpanded && "lg:hidden",
          )}
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <button
              type="button"
              aria-expanded={openTransferRequestsExpanded}
              aria-controls="open-transfer-requests-panel"
              onClick={() => setOpenTransferRequestsExpanded((current) => !current)}
              className={cn(
                "flex min-w-0 flex-1 items-center gap-2 text-left",
                "rounded-md focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500 focus-visible:ring-offset-2",
              )}
            >
              {openTransferRequestsExpanded ? (
                <>
                  <ChevronDown className="h-4 w-4 shrink-0 text-slate-500 lg:hidden" aria-hidden />
                  <ChevronLeft className="hidden h-4 w-4 shrink-0 text-slate-500 lg:inline" aria-hidden />
                </>
              ) : (
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" aria-hidden />
              )}
              <CardTitle className="mb-0">
                Open transfer requests{" "}
                <span className="text-slate-500">({filteredRequests.length})</span>
              </CardTitle>
            </button>
            <div className="flex flex-wrap items-center gap-2">
              {selectedRequestIds.size > 0 ? (
                <span className="text-xs text-slate-600">
                  {selectedRequestIds.size} selected
                  {sharedLane == null ? (
                    <span className="ml-2 rounded bg-amber-50 px-2 py-0.5 font-medium text-amber-800">
                      mixed lanes
                    </span>
                  ) : null}
                </span>
              ) : null}
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="You do not have permission to convert transfer requests in Manufacturing management."
                layout="inline"
              >
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!hubWritesAllowed || selectedRequestIds.size === 0 || sharedLane == null}
                  onClick={() => {
                    openConvertDialog("new-order", null, Array.from(selectedRequestIds));
                  }}
                >
                  Convert selected to new order…
                </Button>
              </ReadOnlyHoverLayer>
            </div>
          </div>
          {openTransferRequestsExpanded ? (
          <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
            <div className="min-w-[16rem] basis-full sm:basis-auto sm:min-w-[18rem]">
              <Label htmlFor="transfer-requests-search">Search open requests</Label>
              <Input
                id="transfer-requests-search"
                type="search"
                value={requestSearch}
                onChange={(event) => setRequestSearch(event.target.value)}
                placeholder="SKU, description, notes…"
              />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Origin site</span>
                {originFilter.size > 0 ? (
                  <button
                    type="button"
                    className="text-xs font-medium text-brand-700 hover:text-brand-900"
                    onClick={() => setOriginFilter(new Set())}
                  >
                    Clear origin filters
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">All origins</span>
                )}
              </div>
              <TransferLocationFilterButtons
                locations={boardQuery.data?.locations ?? []}
                idPrefix="request-origin"
                isActive={(locationId) => originFilter.has(locationId)}
                onToggle={toggleOriginFilter}
              />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Destination site</span>
                {destinationFilter.size > 0 ? (
                  <button
                    type="button"
                    className="text-xs font-medium text-brand-700 hover:text-brand-900"
                    onClick={() => setDestinationFilter(new Set())}
                  >
                    Clear destination filters
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">All destinations</span>
                )}
              </div>
              <TransferLocationFilterButtons
                locations={boardQuery.data?.locations ?? []}
                idPrefix="request-destination"
                isActive={(locationId) => destinationFilter.has(locationId)}
                onToggle={toggleDestinationFilter}
              />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Reason</span>
                {requestReasonFilter.size > 0 ? (
                  <button
                    type="button"
                    className="text-xs font-medium text-brand-700 hover:text-brand-900"
                    onClick={() => setRequestReasonFilter(new Set())}
                  >
                    Clear reason filters
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">All reasons</span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {reasonFilterOptions.map((reason) => {
                  const active = requestReasonFilter.has(reason);
                  return (
                    <button
                      key={reason}
                      type="button"
                      aria-pressed={active}
                      onClick={() => toggleRequestReasonFilter(reason)}
                      className={filterTogglePillClass(active)}
                    >
                      {reasonLabel(reason)}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="min-w-[12rem]">
              <Label htmlFor="transfer-request-target-month">Target month</Label>
              <select
                id="transfer-request-target-month"
                className={FILTER_SELECT_CLASS}
                value={requestTargetMonthFilter}
                onChange={(event) => setRequestTargetMonthFilter(event.target.value)}
              >
                <option value="">All target months</option>
                <option value={REQUEST_TARGET_DATE_OVERDUE}>Overdue</option>
                {requestTargetMonthOptions.map((monthKey) => (
                  <option key={`request-target-month-${monthKey}`} value={monthKey}>
                    {formatTransferRequestTargetMonthLabel(monthKey)}
                  </option>
                ))}
              </select>
            </div>
          </div>
          ) : null}
        </CardHeader>
        {openTransferRequestsExpanded ? (
        <CardBody id="open-transfer-requests-panel" className="flex min-h-0 flex-1 flex-col p-0">
          <div
            className="min-h-0 flex-1 overflow-auto"
            onPointerEnter={enableBoardSkuHintsFetch}
          >
          {boardQuery.isLoading ? (
            <p className="py-8 text-center text-sm text-slate-600">Loading…</p>
          ) : boardQuery.isError ? (
            <p className="py-8 text-center text-sm text-rose-600">
              Couldn’t load the transfers board: {boardQuery.error.message}
            </p>
          ) : filteredRequests.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-600">
              No open transfer requests
              {requestReasonFilter.size > 0
                || requestTargetMonthFilter !== ""
                || normalizedRequestSearch !== ""
                ? " match these filters"
                : ""}.
            </p>
          ) : (
            <table className="min-w-full border-collapse border border-slate-200 text-sm">
              <thead className="sticky top-0 z-10 bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500 shadow-[0_1px_0_0_rgb(226_232_240)]">
                <tr>
                  <th className="w-8 border-b border-slate-200 px-2 py-2"> </th>
                  <th className="border-b border-slate-200 px-2 py-2">SKU</th>
                  <th className="border-b border-slate-200 px-2 py-2">Lane</th>
                  <th className="border-b border-slate-200 px-2 py-2">Target date</th>
                  <TransferOriginOnHandHeader className="border-b border-slate-200 px-2 py-2 text-right" />
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Requested</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Remaining</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right"> </th>
                </tr>
              </thead>
              <tbody>
                {filteredRequests.map((row) => {
                  const isSelected = selectedRequestIds.has(row.id);
                  const originOnHand = lookupOriginOnHand(row.origin_location_id, row.sku_id);
                  const remainder = row.allocated_quantity > 0;
                  const cancelButton = (
                    <Button
                      variant="secondary"
                      size="sm"
                      className={MRP_TRANSFER_REQUEST_CANCEL_BUTTON_CLASS}
                      disabled={!hubWritesAllowed || cancelRequestMutation.isPending}
                      onClick={() => {
                        if (
                          window.confirm(
                            mrpTransferRequestCancelConfirmMessage({
                              sku: row.sku_id,
                              remainingQuantity: row.remaining_quantity,
                              hasAllocation: remainder,
                            }),
                          )
                        ) {
                          cancelRequestMutation.mutate({ id: row.id, remainder });
                        }
                      }}
                    >
                      {mrpTransferRequestCancelButtonLabel(remainder)}
                    </Button>
                  );
                  return (
                    <tr
                      key={row.id}
                      draggable={hubWritesAllowed}
                      onDragStart={(event) => handleRequestDragStart(event, row)}
                      onDragEnd={handleRequestDragEnd}
                      className={cn(
                        "border-b border-slate-100 last:border-b-0",
                        isSelected ? "bg-brand-50" : "hover:bg-slate-50",
                        hubWritesAllowed ? "cursor-grab active:cursor-grabbing" : "cursor-default",
                      )}
                    >
                      <td className="px-2 py-2 align-top">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleRequestSelected(row.id)}
                          aria-label={`Select transfer request ${row.id}`}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <span className="inline-flex items-center gap-1">
                          <TransferSkuHoverCell
                            sku={row.sku_id}
                            hint={boardSkuHoverHints?.[row.sku_id.trim()]}
                            hintsLoading={boardSkuHintsLoading}
                            onHoverIntent={enableBoardSkuHintsFetch}
                          />
                          <HoverHintWithGlyph
                            hoverHintContent={
                              <TransferRequestReasonDetail row={row} reasonLabel={reasonLabel} />
                            }
                            hoverHintAriaLabel={`Reason: ${reasonLabel(row.reason)}`}
                            size="sm"
                          />
                        </span>
                      </td>
                      <td className="px-2 py-2">
                        <TransferLocationRoute
                          originLocation={locationsById.get(row.origin_location_id)}
                          originLocationId={row.origin_location_id}
                          destinationLocation={locationsById.get(row.destination_location_id)}
                          destinationLocationId={row.destination_location_id}
                        />
                      </td>
                      <td className="px-2 py-2">
                        <TransferRequestTargetDateCell
                          row={row}
                          hubWritesAllowed={hubWritesAllowed}
                          hubReadOnlyHoverHint="You do not have permission to edit transfer requests in Manufacturing management."
                          editing={editingRequestTargetDateId === row.id}
                          onStartEdit={() => setEditingRequestTargetDateId(row.id)}
                          onDismissEdit={() => setEditingRequestTargetDateId(null)}
                          onCommit={(value) => {
                            if (value != null && value.trim() !== "") {
                              updateRequestTargetDateMutation.mutate({ id: row.id, target_date: value });
                            }
                          }}
                          pending={updateRequestTargetDateMutation.isPending && updateRequestTargetDateMutation.variables?.id === row.id}
                          commitError={updateRequestTargetDateMutation.isError && updateRequestTargetDateMutation.variables?.id === row.id
                            ? updateRequestTargetDateMutation.error.message
                            : null}
                        />
                      </td>
                      <TransferOriginOnHandCell
                        value={originOnHand.value}
                        loading={originInventoryLoading}
                        siteMissing={originOnHand.siteMissing}
                        compareQuantity={row.remaining_quantity}
                        sku={row.sku_id}
                        originSiteCode={mrpSiteCodeForLocationId(locationsById, row.origin_location_id)}
                        className="px-2 py-2"
                      />
                      <td className="px-2 py-2 text-right tabular-nums text-slate-500">
                        {row.requested_quantity}
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-900">
                        {row.remaining_quantity}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint="You do not have permission to cancel transfer requests in Manufacturing management."
                          layout="inline"
                        >
                          {!remainder || !hubWritesAllowed ? (
                            cancelButton
                          ) : (
                            <HoverHintTrigger align="end" trigger={cancelButton}>
                              {MRP_TRANSFER_REQUEST_CANCEL_REMAINDER_HOVER}
                            </HoverHintTrigger>
                          )}
                        </ReadOnlyHoverLayer>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
          </div>
        </CardBody>
        ) : null}
      </Card>

      <HubViewportCard className="min-w-0">
        <CardHeader className="shrink-0 space-y-3">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <CardTitle className="mb-0">
              Transfer orders <span className="text-slate-500">({filteredOrders.length})</span>
            </CardTitle>
            <div className="flex flex-wrap items-end gap-2">
              <div className="min-w-[12rem]">
                <Label htmlFor="transfer-orders-sort">Sort</Label>
                <select
                  id="transfer-orders-sort"
                  className={FILTER_SELECT_CLASS}
                  value={orderSortKey}
                  onChange={(event) => setOrderSortKey(event.target.value as TransferOrderSortKey)}
                >
                  {TRANSFER_ORDER_SORT_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="You do not have permission to create transfer orders in Manufacturing management."
                layout="inline"
              >
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={!hubWritesAllowed}
                  onClick={() => {
                    setCreateTransferDialogOpen(true);
                    setNewTransferDraft((prev) => ({
                      ...EMPTY_NEW_TRANSFER_DRAFT,
                      ship_by_date: prev.ship_by_date || todayIsoYyyyMmDd(),
                    }));
                  }}
                >
                  Create transfer…
                </Button>
              </ReadOnlyHoverLayer>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <Label htmlFor="transfer-orders-search">Search transfer orders</Label>
              <Input
                id="transfer-orders-search"
                type="search"
                value={orderSearch}
                onChange={(event) => setOrderSearch(event.target.value)}
                placeholder="Transfer number, SKU, notes…"
              />
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Status</span>
              <div className="mt-1 flex flex-wrap items-center gap-1">
                {TRANSFER_ORDER_STATUS_VALUES.map((status) => {
                  const active = orderStatusFilter === status;
                  return (
                    <button
                      key={status}
                      type="button"
                      aria-pressed={active}
                      onClick={() => selectOrderStatusFilter(status)}
                      className={filterTogglePillClass(active)}
                    >
                      {TRANSFER_ORDER_STATUS_LABELS[status]}
                    </button>
                  );
                })}
              </div>
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Origin site</span>
                {orderOriginFilter != null ? (
                  <button
                    type="button"
                    className="text-xs font-medium text-brand-700 hover:text-brand-900"
                    onClick={() => setOrderOriginFilter(null)}
                  >
                    Clear origin filter
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">All origins</span>
                )}
              </div>
              <TransferLocationFilterButtons
                locations={boardQuery.data?.locations ?? []}
                idPrefix="order-origin"
                isActive={(locationId) => orderOriginFilter === locationId}
                onToggle={selectOrderOriginFilter}
              />
            </div>
            <div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Destination site</span>
                {orderDestinationFilter != null ? (
                  <button
                    type="button"
                    className="text-xs font-medium text-brand-700 hover:text-brand-900"
                    onClick={() => setOrderDestinationFilter(null)}
                  >
                    Clear destination filter
                  </button>
                ) : (
                  <span className="text-xs text-slate-500">All destinations</span>
                )}
              </div>
              <TransferLocationFilterButtons
                locations={boardQuery.data?.locations ?? []}
                idPrefix="order-destination"
                isActive={(locationId) => orderDestinationFilter === locationId}
                onToggle={selectOrderDestinationFilter}
              />
            </div>
          </div>
        </CardHeader>
        <CardBody className="flex min-h-0 flex-1 flex-col p-0">
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto p-3">
          {boardQuery.isLoading ? (
            <p className="py-8 text-center text-sm text-slate-600">Loading…</p>
          ) : boardQuery.isError ? (
            <p className="py-8 text-center text-sm text-rose-600">
              Couldn’t load the transfers board: {boardQuery.error.message}
            </p>
          ) : filteredOrders.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-600">
              No transfer orders
              {orderStatusFilter != null
                || orderOriginFilter != null
                || orderDestinationFilter != null
                || normalizedOrderSearch !== ""
                ? " match these filters"
                : ""}
              {normalizedOrderSearch !== "" ? ` matching “${orderSearch.trim()}”` : ""}.
            </p>
          ) : (
            filteredOrders.map((order) => {
              const isExpanded = expandedOrderIds.has(order.id);
              const isSelected = selectedOrderId === order.id;
              const isPending = order.status === "pending";
              const isTerminal = TRANSFER_ORDER_TERMINAL_STATUSES.has(order.status);
              const nextAction = primaryNextActionForTransferOrder(order);
              const revertToPendingAction = revertToPendingActionFor(order.status);
              const showPrePackLink = transferOrderShowsPrePackLink(order);
              const showTransferOrderPdfLink = transferOrderShowsTransferOrderPdfLink(order);
              const destinationLocation = locationsById.get(order.destination_location_id);
              const showCommercialInvoice = isInternationalShipTo(
                destinationLocation?.ship_country_code,
                destinationLocation?.ship_state,
              );
              const showOutboundLink = transferOrderShowsOutboundLink(order);
              const pendingPackQtyRequests = countPendingTransferPackingQuantityRequests(order);
              const inventoryShortfall = transferOrderHasOriginInventoryShortfall(
                order,
                lookupOriginOnHand,
                { loading: originInventoryLoading },
              );
              const packedByItemId = transferOrderPackedQuantityByItemId(order);
              const hasSealedCartons = transferOrderHasSealedCartons(order);
              const packingWasStarted =
                order.packing_started_at != null
                || pendingPackQtyRequests > 0
                || order.boxes.length > 0
                || order.allocations.length > 0;
              const usesHubShipRoute =
                order.status === "ready_to_ship"
                && nextAction?.to === "shipped"
                && transferOrderIsSgsOrigin(order);
              const lifecycleActionPending =
                transitionMutation.isPending || shipTransferMutation.isPending;
              return (
                <div
                  key={order.id}
                  id={`transfer-order-${order.id}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => setSelectedOrderId(isSelected ? null : order.id)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") {
                      event.preventDefault();
                      setSelectedOrderId(isSelected ? null : order.id);
                    }
                  }}
                  onDragOver={(event) => isPending && handleDragOverTarget(event, order.id)}
                  onDragLeave={() => isPending && handleDragLeaveTarget(order.id)}
                  onDrop={(event) => isPending && handleDropOnTarget(event, order.id)}
                  className={cn(
                    "cursor-pointer rounded-md border bg-white p-2 shadow-sm transition",
                    isSelected ? "border-brand-500 ring-2 ring-brand-200" : "border-slate-200 hover:border-slate-300",
                    dragOverTarget === order.id ? "border-brand-500 bg-brand-50" : "",
                    isTerminal ? "opacity-80" : "",
                  )}
                >
                  <div className="space-y-1.5">
                    <div className="flex gap-1.5">
                      <button
                        type="button"
                        aria-expanded={isExpanded}
                        aria-label={isExpanded ? "Collapse transfer lines" : "Expand transfer lines"}
                        className="flex h-7 w-7 shrink-0 items-center justify-center self-start rounded text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                        onClick={(event) => {
                          event.stopPropagation();
                          toggleOrderExpanded(order.id);
                        }}
                      >
                        {isExpanded ? (
                          <ChevronDown className="h-4 w-4" aria-hidden />
                        ) : (
                          <ChevronRight className="h-4 w-4" aria-hidden />
                        )}
                      </button>
                      <div className="min-w-0 flex-1 space-y-0.5">
                        <div className="flex min-w-0 flex-wrap items-center gap-1">
                          <p className="text-sm font-semibold text-slate-900">
                            {order.transfer_number ?? `Transfer #${String(order.id)}`}
                          </p>
                          <TransferLocationRoute
                            originLocation={locationsById.get(order.origin_location_id)}
                            originLocationId={order.origin_location_id}
                            destinationLocation={locationsById.get(order.destination_location_id)}
                            destinationLocationId={order.destination_location_id}
                            size="md"
                          />
                          <Badge tone={transferOrderStatusBadgeTone(order.status)}>
                            {TRANSFER_ORDER_STATUS_LABELS[order.status]}
                          </Badge>
                          {order.ship_by_date != null ? (
                            <Badge tone="neutral" className="tabular-nums">
                              {order.ship_by_date}
                            </Badge>
                          ) : null}
                          {order.lines.length > 0 ? (
                            <Badge tone="muted" className="tabular-nums">
                              {String(order.lines.length)} line{order.lines.length === 1 ? "" : "s"}
                            </Badge>
                          ) : null}
                          {order.purchase_order_id != null ? (
                            <span
                              title={
                                order.purchase_order_received
                                  ? "The linked PO has a submitted receiving receipt — stock has arrived at SGS."
                                  : "Waiting on the linked PO to be received at SGS."
                              }
                            >
                              <Badge tone={order.purchase_order_received ? "success" : "warning"}>
                                {order.purchase_order_number ?? `PO #${String(order.purchase_order_id)}`}
                                {order.purchase_order_received ? " received" : " awaited"}
                              </Badge>
                            </span>
                          ) : null}
                          {order.mv_sync_pending ? (
                            <Badge tone="warning">MV sync pending</Badge>
                          ) : null}
                          {order.mv_sync_error != null ? (
                            <Badge tone="danger">MV sync error</Badge>
                          ) : null}
                          {pendingPackQtyRequests > 0 ? (
                            <HoverHintContent hoverHint={TRANSFER_PENDING_QUANTITY_CHANGE_HOVER}>
                              <Badge tone="danger">
                                {transferPendingQuantityChangeChipLabel(pendingPackQtyRequests)}
                              </Badge>
                            </HoverHintContent>
                          ) : null}
                          {inventoryShortfall ? (
                            <HoverHintContent hoverHint={TRANSFER_INVENTORY_SHORTFALL_HOVER}>
                              <Badge tone="danger">{TRANSFER_INVENTORY_SHORTFALL_CHIP_LABEL}</Badge>
                            </HoverHintContent>
                          ) : null}
                          {isPending && packingWasStarted ? (
                            <Badge tone="muted">Packing started previously</Badge>
                          ) : null}
                        </div>
                        {order.notes != null && order.notes.trim() !== "" ? (
                          <p className="min-w-0 text-xs text-slate-600">{order.notes.trim()}</p>
                        ) : null}
                        {order.outbound_tracking != null && order.outbound_tracking.trim() !== "" ? (
                          <p className="min-w-0 text-xs text-slate-700">
                            Tracking
                            {order.outbound_carrier != null && order.outbound_carrier.trim() !== ""
                              ? ` (${order.outbound_carrier.trim()})`
                              : ""}
                            :{" "}
                            <span className="font-mono tabular-nums">{order.outbound_tracking.trim()}</span>
                          </p>
                        ) : order.outbound_carrier != null && order.outbound_carrier.trim() !== "" ? (
                          <p className="min-w-0 text-xs text-slate-700">
                            Carrier: {order.outbound_carrier.trim()}
                          </p>
                        ) : null}
                        <TransferPackLaborInline
                          expanded={isExpanded}
                          labor={boardPackLaborQuery.data?.laborByTransferOrderId[String(order.id)]}
                          isLoading={boardPackLaborQuery.isLoading}
                          isError={boardPackLaborQuery.isError}
                        />
                        <TransferShipmentEventsInline order={order} />
                      </div>
                    </div>
                    <div
                      className="flex flex-wrap items-stretch gap-1 [&_.group\\/readonly]:inline-flex [&_.group\\/readonly>div]:flex"
                      onClick={(event) => event.stopPropagation()}
                    >
                      {pendingPackQtyRequests > 0 ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                          onClick={(event) => {
                            stopTransferCardButtonClick(event);
                            setPackQtyApprovalsOrderId(order.id);
                          }}
                        >
                          {transferCardActionContent("review_approvals")}
                        </Button>
                      ) : null}
                      {hasSealedCartons && transferOrderIsSgsOrigin(order) ? (
                        <a
                          href={`/shipping/pre-pack/transfers/${String(order.id)}`}
                          target="_blank"
                          rel="noreferrer"
                          className={TRANSFER_CARD_STACKED_LINK_CLASS}
                          onClick={stopTransferCardLinkClick}
                        >
                          {transferCardActionContent("reprint_carton_labels", {
                            icon: <Printer className="h-3.5 w-3.5 shrink-0" aria-hidden />,
                          })}
                        </a>
                      ) : null}
                      {showCommercialInvoice ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                          onClick={(event) => {
                            stopTransferCardButtonClick(event);
                            setCommercialInvoiceOrderId(order.id);
                          }}
                        >
                          {transferCardActionContent("commercial_invoice")}
                        </Button>
                      ) : null}
                      {showTransferOrderPdfLink ? (
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                          disabled={transferOrderPdfMutation.isPending}
                          onClick={(event) => {
                            stopTransferCardButtonClick(event);
                            transferOrderPdfMutation.mutate({
                              orderId: order.id,
                              transferNumber: order.transfer_number,
                            });
                          }}
                        >
                          {transferCardActionContent("download_pdf", {
                            icon: <Download className="h-3.5 w-3.5 shrink-0" aria-hidden />,
                          })}
                        </Button>
                      ) : null}
                      {showPrePackLink ? (
                        <a
                          href={transferOrderPrePackHref(order)}
                          target="_blank"
                          rel="noreferrer"
                          className={TRANSFER_CARD_STACKED_LINK_CLASS}
                          onClick={stopTransferCardLinkClick}
                        >
                          {transferCardActionContent("open_in_pre_pack", {
                            icon: <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />,
                          })}
                        </a>
                      ) : null}
                      {showOutboundLink ? (
                        <a
                          href="/shipping/outbound"
                          target="_blank"
                          rel="noreferrer"
                          className={TRANSFER_CARD_STACKED_LINK_CLASS}
                          onClick={stopTransferCardLinkClick}
                        >
                          {transferCardActionContent("open_in_outbound", {
                            icon: <ExternalLink className="h-3.5 w-3.5 shrink-0" aria-hidden />,
                          })}
                        </a>
                      ) : null}
                      {transferOrderShowsEditTracking(order) ? (
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint="You do not have permission to edit transfer tracking."
                          layout="inline"
                        >
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                            disabled={!hubWritesAllowed || outboundTrackingMutation.isPending}
                            onClick={(event) => {
                              stopTransferCardButtonClick(event);
                              const nextCarrier = window.prompt(
                                "Carrier (blank to clear)",
                                order.outbound_carrier ?? "",
                              );
                              if (nextCarrier === null) return;
                              const nextTracking = window.prompt(
                                "Tracking number (blank to clear)",
                                order.outbound_tracking ?? "",
                              );
                              if (nextTracking === null) return;
                              outboundTrackingMutation.mutate({
                                orderId: order.id,
                                carrier: nextCarrier.trim() === "" ? null : nextCarrier.trim(),
                                tracking: nextTracking.trim() === "" ? null : nextTracking.trim(),
                              });
                            }}
                          >
                            {transferCardActionContent("edit_tracking")}
                          </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {nextAction != null && !isTerminal ? (
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint="You do not have permission to advance transfer orders."
                          layout="inline"
                        >
                          <Button
                            type="button"
                            variant="primary"
                            size="sm"
                            className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                            disabled={!hubWritesAllowed || lifecycleActionPending}
                            onClick={(event) => {
                              stopTransferCardButtonClick(event);
                              if (usesHubShipRoute) {
                                shipTransferMutation.mutate({ orderId: order.id });
                                return;
                              }
                              transitionMutation.mutate({ orderId: order.id, to: nextAction.to });
                            }}
                            >
                              {transferCardActionContent(
                                transferCardActionKeyForPrimaryLabel(nextAction.label)!,
                              )}
                            </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {revertToPendingAction != null && !isTerminal ? (
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint="You do not have permission to revert transfer orders to pending."
                          layout="inline"
                        >
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                            disabled={!hubWritesAllowed || transitionMutation.isPending}
                            onClick={(event) => {
                              stopTransferCardButtonClick(event);
                              if (
                                window.confirm(
                                  `Revert transfer order ${order.transfer_number ?? `#${String(order.id)}`} to pending? You can edit lines again; pre-pack links will be hidden until you lock for packing.`,
                                )
                              ) {
                                transitionMutation.mutate({
                                  orderId: order.id,
                                  to: revertToPendingAction.to,
                                });
                              }
                            }}
                          >
                            {transferCardActionContent("revert_to_pending")}
                          </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {(order.status === "shipped" || order.status === "delivered" || order.status === "partially_received") ? (
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint="You do not have permission to receive transfers."
                          layout="inline"
                        >
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                            disabled={!hubWritesAllowed}
                            onClick={(event) => {
                              stopTransferCardButtonClick(event);
                              openReceiveDialog(order);
                            }}
                          >
                            {transferCardActionContent("receive_transfer")}
                          </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {isPending ? (
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint="You do not have permission to edit pending transfer orders."
                          layout="inline"
                        >
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                            disabled={!hubWritesAllowed || editPendingMutation.isPending}
                            onClick={(event) => {
                              stopTransferCardButtonClick(event);
                              openEditPendingDialog(order);
                            }}
                          >
                            {transferCardActionContent("edit")}
                          </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                      {!isTerminal ? (
                        <ReadOnlyHoverLayer
                          active={!hubWritesAllowed}
                          hoverHint="You do not have permission to cancel transfer orders."
                          layout="inline"
                        >
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className={TRANSFER_CARD_STACKED_BUTTON_CLASS}
                            disabled={!hubWritesAllowed || transitionMutation.isPending}
                            onClick={(event) => {
                              stopTransferCardButtonClick(event);
                              setCancelTransferConfirm({
                                orderId: order.id,
                                transferNumber: order.transfer_number,
                              });
                            }}
                          >
                            {transferCardActionContent("cancel")}
                          </Button>
                        </ReadOnlyHoverLayer>
                      ) : null}
                    </div>
                  </div>
                  {isExpanded ? (
                    <div className="mt-3 space-y-4 border-t border-slate-100 pt-3">
                      <table className="min-w-full border-collapse text-xs">
                        <thead className="text-slate-500">
                          <tr>
                            <th className="border-b border-slate-100 px-2 py-1 text-left">#</th>
                            <th className="border-b border-slate-100 px-2 py-1 text-left">SKU</th>
                            <TransferOriginOnHandHeader className="border-b border-slate-100 px-2 py-1 text-right" />
                            <th className="border-b border-slate-100 px-2 py-1 text-right">Qty</th>
                            <th className="border-b border-slate-100 px-2 py-1 text-right">Packed</th>
                            <th className="border-b border-slate-100 px-2 py-1 text-right">Received</th>
                            <th className="border-b border-slate-100 px-2 py-1 text-left">Reason</th>
                            <th className="border-b border-slate-100 px-2 py-1 text-left">Source</th>
                          </tr>
                        </thead>
                        <tbody>
                          {order.lines.length === 0 ? (
                            <tr>
                              <td colSpan={8} className="px-2 py-2 text-center text-slate-500">
                                No lines yet
                                {isPending ? " — drop a request onto this order to add one." : "."}
                              </td>
                            </tr>
                          ) : (
                            order.lines.map((line) => {
                              const lineOnHand = lookupOriginOnHand(order.origin_location_id, line.sku_id);
                              const packedQuantity = packedByItemId.get(line.id) ?? 0;
                              const receivedQuantity = line.received_quantity ?? 0;
                              const packTarget = effectiveTransferPackQuantity(line);
                              return (
                                <tr key={line.id} className="border-b border-slate-100 last:border-b-0">
                                  <td className="px-2 py-1 tabular-nums text-slate-600">{line.line_number ?? "—"}</td>
                                  <td className="px-2 py-1">
                                    <TransferSkuHoverCell
                                      sku={line.sku_id ?? ""}
                                      hint={boardSkuHoverHints?.[(line.sku_id ?? "").trim()]}
                                      hintsLoading={boardSkuHintsLoading}
                                      onHoverIntent={enableBoardSkuHintsFetch}
                                      extraHint={<TransferLinePickPackHoverHint order={order} line={line} />}
                                    />
                                  </td>
                                  <TransferOriginOnHandCell
                                    value={lineOnHand.value}
                                    loading={originInventoryLoading}
                                    siteMissing={lineOnHand.siteMissing}
                                    compareQuantity={packTarget}
                                    sku={line.sku_id}
                                    originSiteCode={mrpSiteCodeForLocationId(locationsById, order.origin_location_id)}
                                    className="px-2 py-1"
                                  />
                                  <td
                                    className={`px-2 py-1 text-right tabular-nums ${packTarget !== line.quantity ? "font-medium text-amber-800" : ""}`}
                                  >
                                    {packTarget}
                                  </td>
                                  <td className="px-2 py-1 text-right tabular-nums">{packedQuantity}</td>
                                  <td
                                    className={`px-2 py-1 text-right tabular-nums ${transferLineReceivedQuantityClassName(receivedQuantity, packedQuantity)}`}
                                  >
                                    {receivedQuantity}
                                  </td>
                                  <td className="px-2 py-1 text-slate-600">{reasonLabel(line.reason)}</td>
                                  <td className="px-2 py-1 text-slate-600">
                                    {line.fulfilled_transfer_request_id != null
                                      ? `Request #${line.fulfilled_transfer_request_id}`
                                      : "Ad-hoc"}
                                  </td>
                                </tr>
                              );
                            })
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              );
            })
          )}
          {requestDragActive && hubWritesAllowed ? (
            <div
              onDragOver={(event) => handleDragOverTarget(event, "new")}
              onDragLeave={() => handleDragLeaveTarget("new")}
              onDrop={(event) => handleDropOnTarget(event, "new")}
              className={cn(
                "rounded-md border-2 border-dashed px-3 py-3 text-center text-xs transition",
                dragOverTarget === "new"
                  ? "border-brand-500 bg-brand-50 text-brand-800"
                  : "border-slate-300 bg-slate-50 text-slate-600",
              )}
            >
              <p className="font-medium text-slate-700">Drop requests here for a new order</p>
              <p className="mt-0.5 text-[11px] leading-snug text-slate-500">
                Creates a pending transfer order from the dropped requests. All dropped requests
                must share origin and destination.
              </p>
            </div>
          ) : null}
          </div>
        </CardBody>
      </HubViewportCard>
      </div>

      {selectedOrder != null ? (
        <TransferOrderDetailPanel
          order={selectedOrder}
          hubWritesAllowed={hubWritesAllowed}
          lookupOriginOnHand={lookupOriginOnHand}
          originInventoryLoading={originInventoryLoading}
          skuHoverHints={boardSkuHoverHints}
        />
      ) : null}

      {commercialInvoiceOrderId != null ? (
        <TransferCommercialInvoiceModal
          transferOrderId={commercialInvoiceOrderId}
          transferNumber={
            (boardQuery.data?.orders ?? []).find((row) => row.id === commercialInvoiceOrderId)?.transfer_number
              ?? null
          }
          onClose={() => setCommercialInvoiceOrderId(null)}
        />
      ) : null}

      <TransferPackingQuantityApprovalsModal
        order={packQtyApprovalsOrder}
        allOrders={boardQuery.data?.orders ?? []}
        hubWritesAllowed={hubWritesAllowed}
        onClose={() => setPackQtyApprovalsOrderId(null)}
      />

      {cancelTransferConfirm != null ? (
        <div
          className="fixed inset-0 z-[56] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-cancel-confirm-title"
          onClick={() => setCancelTransferConfirm(null)}
        >
          <div
            className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="transfer-cancel-confirm-title" className="text-lg font-semibold text-slate-900">
              Cancel transfer order?
            </h2>
            <p className="mt-2 text-sm text-slate-600">
              Cancel{" "}
              <span className="font-medium text-slate-900">
                {cancelTransferConfirm.transferNumber ?? `#${String(cancelTransferConfirm.orderId)}`}
              </span>
              ? The order will move to cancelled and will no longer appear in active workflows.
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <Button
                type="button"
                variant="secondary"
                onClick={() => setCancelTransferConfirm(null)}
              >
                Keep transfer
              </Button>
              <ReadOnlyHoverLayer
                active={!hubWritesAllowed}
                hoverHint="You do not have permission to cancel transfer orders."
                layout="inline"
              >
                <Button
                  type="button"
                  variant="danger"
                  disabled={!hubWritesAllowed || transitionMutation.isPending}
                  onClick={() => {
                    transitionMutation.mutate(
                      { orderId: cancelTransferConfirm.orderId, to: "cancelled" },
                      { onSettled: () => setCancelTransferConfirm(null) },
                    );
                  }}
                >
                  Cancel transfer
                </Button>
              </ReadOnlyHoverLayer>
            </div>
          </div>
        </div>
      ) : null}

      {/* ----- Convert dialog ----- */}
      {convertDialog != null ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-convert-dialog-title"
        >
          <div className="max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-lg bg-white p-4 shadow-xl">
            <h2 id="transfer-convert-dialog-title" className="text-lg font-semibold text-slate-900">
              {convertDialog.mode === "new-order"
                ? "Create pending transfer order from requests"
                : `Add requests to ${
                    (boardQuery.data?.orders ?? []).find((order) => order.id === convertDialog.transferOrderId)?.transfer_number ??
                    `Transfer #${String(convertDialog.transferOrderId ?? "")}`
                  }`}
            </h2>
            <p className="mt-1 text-xs text-slate-600">
              Adjust the per-request quantity to allocate. Allocating less than the remaining
              quantity is supported and leaves the request in the open queue as partially
              allocated; the unfulfilled remainder can be allocated to another order later.
            </p>

            {convertDialog.mode === "new-order" ? (
              <div className="mt-3">
                <Label htmlFor="convert-ship-by">Ship by</Label>
                <Input
                  id="convert-ship-by"
                  type="date"
                  value={convertDialog.shipByDate}
                  onChange={(event) =>
                    setConvertDialog((current) =>
                      current ? { ...current, shipByDate: event.target.value } : current,
                    )
                  }
                />
              </div>
            ) : null}

            <table className="mt-3 min-w-full border-collapse text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="border-b border-slate-200 px-2 py-2">SKU</th>
                  <th className="border-b border-slate-200 px-2 py-2">Reason</th>
                  <TransferOriginOnHandHeader className="border-b border-slate-200 px-2 py-2 text-right" />
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Remaining</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Allocate</th>
                </tr>
              </thead>
              <tbody>
                {convertDialog.requestIds.map((id) => {
                  const requestRow = (boardQuery.data?.requests ?? []).find((row) => row.id === id);
                  const originOnHand =
                    requestRow != null
                      ? lookupOriginOnHand(requestRow.origin_location_id, requestRow.sku_id)
                      : { value: null, siteMissing: true };
                  const allocateQty = Number.parseInt(convertDialog.quantityOverrides[id] ?? "", 10);
                  return (
                    <tr key={id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-2">
                        <TransferSkuHoverCell
                          sku={requestRow?.sku_id ?? ""}
                          hint={boardSkuHoverHints?.[(requestRow?.sku_id ?? "").trim()]}
                          hintsLoading={boardSkuHintsLoading}
                          onHoverIntent={enableBoardSkuHintsFetch}
                        />
                      </td>
                      <td className="px-2 py-2 text-slate-700">
                        {requestRow != null ? reasonLabel(requestRow.reason) : "—"}
                      </td>
                      <TransferOriginOnHandCell
                        value={originOnHand.value}
                        loading={originInventoryLoading}
                        siteMissing={originOnHand.siteMissing}
                        compareQuantity={Number.isFinite(allocateQty) ? allocateQty : requestRow?.remaining_quantity}
                        sku={requestRow?.sku_id}
                        originSiteCode={
                          requestRow != null
                            ? mrpSiteCodeForLocationId(locationsById, requestRow.origin_location_id)
                            : null
                        }
                        className="px-2 py-2"
                      />
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700">
                        {requestRow?.remaining_quantity ?? "—"}
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          min={1}
                          value={convertDialog.quantityOverrides[id] ?? ""}
                          onChange={(event) =>
                            setConvertDialog((current) =>
                              current
                                ? {
                                    ...current,
                                    quantityOverrides: {
                                      ...current.quantityOverrides,
                                      [id]: event.target.value,
                                    },
                                  }
                                : current,
                            )
                          }
                          className="w-24 text-right"
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            {convertMutation.isError ? (
              <p className="mt-3 text-sm text-rose-700">{convertMutation.error.message}</p>
            ) : null}

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={convertMutation.isPending}
                onClick={() => {
                  setConvertDialog(null);
                  convertMutation.reset();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={convertMutation.isPending}
                onClick={submitConvert}
              >
                {convertDialog.mode === "new-order" ? "Create pending order" : "Add to order"}
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ----- Edit pending order dialog ----- */}
      {editPendingDialog != null ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-edit-pending-dialog-title"
        >
          <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-4 shadow-xl">
            <h2 id="transfer-edit-pending-dialog-title" className="text-lg font-semibold text-slate-900">
              Edit {editPendingDialog.transferNumber ?? `Transfer #${String(editPendingDialog.orderId)}`}
            </h2>
            <p className="mt-1 text-xs text-slate-600">
              Change ship-by date, line reasons, notes, line quantities, or ad-hoc lines while this order is
              still pending. Lines from transfer requests keep their SKU and inherit each request&apos;s reason.
            </p>
            {editPendingDialog.pendingPackQtyRequestCount > 0 ? (
              <p className="mt-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                This order has {editPendingDialog.pendingPackQtyRequestCount} pending pack quantity approval
                {editPendingDialog.pendingPackQtyRequestCount === 1 ? "" : "s"}.{" "}
                <button
                  type="button"
                  className="font-medium underline underline-offset-2 hover:text-amber-950"
                  onClick={() => {
                    setPackQtyApprovalsOrderId(editPendingDialog.orderId);
                    setEditPendingDialog(null);
                  }}
                >
                  Review pack quantity approvals
                </button>
              </p>
            ) : null}

            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <div>
                <Label htmlFor="edit-pending-ship-by">Ship by</Label>
                <Input
                  id="edit-pending-ship-by"
                  type="date"
                  value={editPendingDialog.ship_by_date}
                  onChange={(event) =>
                    setEditPendingDialog((current) =>
                      current ? { ...current, ship_by_date: event.target.value } : current,
                    )
                  }
                />
              </div>
              <div>
                <TransferReasonSelect
                  id="edit-pending-default-reason"
                  label="Default reason for new lines"
                  value={editPendingDialog.default_line_reason}
                  onChange={(reason) =>
                    setEditPendingDialog((current) =>
                      current ? { ...current, default_line_reason: reason } : current,
                    )
                  }
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="mt-1"
                  onClick={() =>
                    setEditPendingDialog((current) =>
                      current
                        ? {
                            ...current,
                            lines: current.lines.map((line) => ({
                              ...line,
                              reason: current.default_line_reason,
                            })),
                          }
                        : current,
                    )
                  }
                >
                  Apply default to all lines
                </Button>
              </div>
            </div>

            <div className="mt-3">
              <Label htmlFor="edit-pending-notes">Notes</Label>
              <Input
                id="edit-pending-notes"
                value={editPendingDialog.notes}
                onChange={(event) =>
                  setEditPendingDialog((current) =>
                    current ? { ...current, notes: event.target.value } : current,
                  )
                }
              />
            </div>

            <div className="mt-3">
              <div className="flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">Lines</span>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setEditPendingDialog((current) =>
                      current
                        ? {
                            ...current,
                            lines: [
                              ...current.lines,
                              {
                                id: null,
                                sku: "",
                                quantity: "",
                                reason: current.default_line_reason,
                                lockedSku: false,
                                sourceLabel: "Ad-hoc",
                                allocatedQuantity: 0,
                              },
                            ],
                          }
                        : current,
                    )
                  }
                >
                  + Add line
                </Button>
              </div>
              {editPendingDialog.lines.length === 0 ? (
                <p className="mt-1 text-xs text-slate-500">At least one line is required.</p>
              ) : (
                <div className="-mx-1 mt-1 overflow-x-auto">
                <table className="min-w-full border-collapse text-sm">
                  <thead className="text-left text-xs text-slate-500">
                    <tr>
                      <th className="px-2 py-1">SKU</th>
                      <TransferOriginOnHandHeader className="px-2 py-1 text-right" />
                      <th className="px-2 py-1 text-right">Qty</th>
                      <th className="px-2 py-1 text-right">Pack size</th>
                      <th className="px-2 py-1 text-right">Packed</th>
                      <th className="px-2 py-1">Reason</th>
                      <th className="px-2 py-1">Source</th>
                      <th className="px-2 py-1 text-right">Move</th>
                      <th className="px-2 py-1 text-right">Remove</th>
                    </tr>
                  </thead>
                  <tbody>
                    {editPendingDialog.lines.map((line, index) => {
                      const lineOnHand = lookupOriginOnHand(editPendingDialog.origin_location_id, line.sku);
                      const lineQty = Number.parseInt(line.quantity, 10);
                      const packSize = lookupEditPendingPackSize(line.sku);
                      const skuLocked = line.lockedSku || line.allocatedQuantity > 0;
                      return (
                      <tr key={line.id ?? `new-${String(index)}`} className="border-b border-slate-100 last:border-b-0">
                        <td className="px-2 py-1">
                          {skuLocked ? (
                            <TransferSkuHoverCell
                              sku={line.sku}
                              hint={
                                editPendingSkuHintsQuery.data?.sku_hover_hints[line.sku.trim()]
                                ?? boardSkuHoverHints?.[line.sku.trim()]
                              }
                              hintsLoading={
                                boardSkuHintsLoading
                                || (editPendingSkuHintsQuery.isPending || editPendingSkuHintsQuery.isFetching)
                              }
                              onHoverIntent={enableBoardSkuHintsFetch}
                            />
                          ) : (
                            <AssemblySkuCombobox
                              value={line.sku}
                              onChange={(next) =>
                                setEditPendingDialog((current) =>
                                  current
                                    ? {
                                        ...current,
                                        lines: current.lines.map((row, rowIndex) =>
                                          rowIndex === index ? { ...row, sku: next } : row,
                                        ),
                                      }
                                    : current,
                                )
                              }
                              onBlurCommittedWithSku={(trimmed) =>
                                setEditPendingDialog((current) =>
                                  current
                                    ? {
                                        ...current,
                                        lines: current.lines.map((row, rowIndex) =>
                                          rowIndex === index ? { ...row, sku: trimmed } : row,
                                        ),
                                      }
                                    : current,
                                )
                              }
                              enforceCatalogSku
                              placeholder="Search SKU…"
                              className="min-w-[12rem]"
                            />
                          )}
                        </td>
                        <TransferOriginOnHandCell
                          value={lineOnHand.value}
                          loading={originInventoryLoading}
                          siteMissing={lineOnHand.siteMissing}
                          compareQuantity={Number.isFinite(lineQty) ? lineQty : undefined}
                          sku={line.sku}
                          originSiteCode={mrpSiteCodeForLocationId(locationsById, editPendingDialog.origin_location_id)}
                          className="px-2 py-1"
                        />
                        <td className="px-2 py-1 text-right">
                          <Input
                            type="number"
                            min={1}
                            value={line.quantity}
                            onChange={(event) =>
                              setEditPendingDialog((current) =>
                                current
                                  ? {
                                      ...current,
                                      lines: current.lines.map((row, rowIndex) =>
                                        rowIndex === index ? { ...row, quantity: event.target.value } : row,
                                      ),
                                    }
                                  : current,
                              )
                            }
                            className="w-24 text-right"
                          />
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                          {packSize != null ? (
                            packSize
                          ) : editPendingSkuHintsQuery.isLoading && line.sku.trim() !== "" ? (
                            "…"
                          ) : (
                            "—"
                          )}
                        </td>
                        <td className="px-2 py-1 text-right tabular-nums text-slate-700">
                          {line.allocatedQuantity > 0 ? line.allocatedQuantity : "—"}
                        </td>
                        <td className="px-2 py-1">
                          <TransferReasonSelect
                            value={line.reason}
                            onChange={(reason) =>
                              setEditPendingDialog((current) =>
                                current
                                  ? {
                                      ...current,
                                      lines: current.lines.map((row, rowIndex) =>
                                        rowIndex === index ? { ...row, reason } : row,
                                      ),
                                    }
                                  : current,
                              )
                            }
                            triggerClassName="min-w-[12rem]"
                          />
                        </td>
                        <td className="px-2 py-1 text-xs text-slate-600">{line.sourceLabel}</td>
                        <td className="px-2 py-1 text-right">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="shrink-0 whitespace-nowrap"
                            disabled={
                              line.id == null
                              || line.allocatedQuantity > 0
                              || editPendingMutation.isPending
                              || moveLineMutation.isPending
                            }
                            title={
                              line.id == null
                                ? "Save the transfer before moving lines."
                                : line.allocatedQuantity > 0
                                  ? "Units are still packed to cartons. Release them in the order detail panel before moving this line."
                                  : undefined
                            }
                            aria-label={
                              line.sku.trim() !== ""
                                ? `Move line ${line.sku.trim()} to another transfer`
                                : "Move line to another transfer"
                            }
                            onClick={() => {
                              if (editPendingDialog == null || line.id == null) return;
                              const targetOrders = listPendingTransferMoveTargets(
                                boardQuery.data?.orders ?? [],
                                editPendingDialog.orderId,
                                editPendingDialog.origin_location_id,
                                editPendingDialog.destination_location_id,
                              );
                              setMoveLineDialog({
                                orderId: editPendingDialog.orderId,
                                transferNumber: editPendingDialog.transferNumber,
                                origin_location_id: editPendingDialog.origin_location_id,
                                destination_location_id: editPendingDialog.destination_location_id,
                                line,
                                targetMode: targetOrders.length > 0 ? "existing" : "new",
                                target_transfer_order_id:
                                  targetOrders[0] != null ? String(targetOrders[0].id) : "",
                                ship_by_date: editPendingDialog.ship_by_date || todayIsoYyyyMmDd(),
                                notes: "",
                              });
                            }}
                          >
                            Move to…
                          </Button>
                        </td>
                        <td className="px-2 py-1 text-right">
                          <Button
                            type="button"
                            variant="secondary"
                            size="sm"
                            className="shrink-0 whitespace-nowrap"
                            disabled={
                              line.allocatedQuantity > 0 || editPendingDialog.lines.length <= 1
                            }
                            title={
                              line.allocatedQuantity > 0
                                ? "Units are still packed to cartons. Release them in the order detail panel before removing this line."
                                : editPendingDialog.lines.length <= 1
                                  ? "At least one line is required on a transfer order."
                                  : undefined
                            }
                            aria-label={
                              line.sku.trim() !== ""
                                ? `Remove line ${line.sku.trim()}`
                                : "Remove line"
                            }
                            onClick={() =>
                              setEditPendingDialog((current) =>
                                current
                                  ? {
                                      ...current,
                                      lines: current.lines.filter((_, rowIndex) => rowIndex !== index),
                                    }
                                  : current,
                              )
                            }
                          >
                            Remove
                          </Button>
                        </td>
                      </tr>
                      );
                    })}
                  </tbody>
                </table>
                </div>
              )}
            </div>

            {editPendingMutation.isError ? (
              <p className="mt-3 text-sm text-rose-700">{editPendingMutation.error.message}</p>
            ) : null}

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={editPendingMutation.isPending}
                onClick={() => {
                  setEditPendingDialog(null);
                  editPendingMutation.reset();
                }}
              >
                Cancel
              </Button>
              <SaveChangesButton
                variant="primary"
                size="sm"
                isDirty={editPendingDirty}
                isLoading={editPendingMutation.isPending}
                onClick={submitEditPending}
              >
                Save changes
              </SaveChangesButton>
            </div>
          </div>
        </div>
      ) : null}

      {/* ----- Move line to another transfer ----- */}
      {moveLineDialog != null ? (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-move-line-dialog-title"
        >
          <div className="w-full max-w-lg rounded-lg bg-white p-4 shadow-xl">
            <h2 id="transfer-move-line-dialog-title" className="text-lg font-semibold text-slate-900">
              Move {moveLineDialog.line.sku.trim() || "line"} to another transfer
            </h2>
            <p className="mt-1 text-xs text-slate-600">
              {buildTransferLocationLabel(
                locationsById.get(moveLineDialog.origin_location_id),
                moveLineDialog.origin_location_id,
              )}{" "}
              →{" "}
              {buildTransferLocationLabel(
                locationsById.get(moveLineDialog.destination_location_id),
                moveLineDialog.destination_location_id,
              )}
              . Only pending transfers on this lane are eligible.
            </p>

            <div className="mt-4 space-y-3">
              {moveLineTargetOrders.length > 0 ? (
                <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
                  <input
                    type="radio"
                    name="move-line-target-mode"
                    className="mt-1"
                    checked={moveLineDialog.targetMode === "existing"}
                    onChange={() =>
                      setMoveLineDialog((current) =>
                        current
                          ? {
                              ...current,
                              targetMode: "existing",
                              target_transfer_order_id:
                                current.target_transfer_order_id
                                || (moveLineTargetOrders[0] != null
                                  ? String(moveLineTargetOrders[0].id)
                                  : ""),
                            }
                          : current,
                      )
                    }
                  />
                  <span className="flex-1">
                    <span className="font-medium">Existing pending transfer</span>
                    <select
                      className="mt-1 block w-full rounded-md border border-slate-300 px-2 py-1.5 text-sm"
                      disabled={moveLineDialog.targetMode !== "existing"}
                      value={moveLineDialog.target_transfer_order_id}
                      onChange={(event) =>
                        setMoveLineDialog((current) =>
                          current
                            ? { ...current, target_transfer_order_id: event.target.value }
                            : current,
                        )
                      }
                    >
                      <option value="">Select transfer…</option>
                      {moveLineTargetOrders.map((order) => (
                        <option key={order.id} value={String(order.id)}>
                          {order.transfer_number ?? `Transfer #${String(order.id)}`}
                          {order.ship_by_date != null ? ` · ship by ${order.ship_by_date}` : ""}
                          {` · ${String(order.lines.length)} line${order.lines.length === 1 ? "" : "s"}`}
                        </option>
                      ))}
                    </select>
                  </span>
                </label>
              ) : null}

              <label className="flex cursor-pointer items-start gap-2 text-sm text-slate-800">
                <input
                  type="radio"
                  name="move-line-target-mode"
                  className="mt-1"
                  checked={moveLineDialog.targetMode === "new"}
                  onChange={() =>
                    setMoveLineDialog((current) =>
                      current ? { ...current, targetMode: "new" } : current,
                    )
                  }
                />
                <span className="flex-1">
                  <span className="font-medium">Create new pending transfer</span>
                  <div className="mt-2 grid gap-2 sm:grid-cols-2">
                    <div>
                      <Label htmlFor="move-line-ship-by">Ship by</Label>
                      <Input
                        id="move-line-ship-by"
                        type="date"
                        disabled={moveLineDialog.targetMode !== "new"}
                        value={moveLineDialog.ship_by_date}
                        onChange={(event) =>
                          setMoveLineDialog((current) =>
                            current ? { ...current, ship_by_date: event.target.value } : current,
                          )
                        }
                      />
                    </div>
                    <div className="sm:col-span-2">
                      <Label htmlFor="move-line-notes">Notes (optional)</Label>
                      <Input
                        id="move-line-notes"
                        disabled={moveLineDialog.targetMode !== "new"}
                        value={moveLineDialog.notes}
                        onChange={(event) =>
                          setMoveLineDialog((current) =>
                            current ? { ...current, notes: event.target.value } : current,
                          )
                        }
                      />
                    </div>
                  </div>
                </span>
              </label>
            </div>

            {moveLineMutation.isError ? (
              <p className="mt-3 text-sm text-rose-700">{moveLineMutation.error.message}</p>
            ) : null}

            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={moveLineMutation.isPending}
                onClick={() => {
                  setMoveLineDialog(null);
                  moveLineMutation.reset();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={moveLineMutation.isPending}
                onClick={() => {
                  if (moveLineDialog.targetMode === "existing") {
                    if (moveLineDialog.target_transfer_order_id.trim() === "") {
                      showToast({ tone: "error", message: "Choose a target pending transfer." });
                      return;
                    }
                  } else if (moveLineDialog.ship_by_date.trim() === "") {
                    showToast({ tone: "error", message: "Ship-by date is required for a new transfer." });
                    return;
                  }
                  moveLineMutation.mutate(moveLineDialog);
                }}
              >
                Move line
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* ----- Create transfer dialog ----- */}
      {createTransferDialogOpen ? (
        <div
          className="fixed inset-0 z-[55] flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-create-dialog-title"
        >
          <div className="max-h-[90vh] w-full max-w-2xl overflow-y-auto rounded-lg bg-white p-4 shadow-xl">
            <h2 id="transfer-create-dialog-title" className="text-lg font-semibold text-slate-900">
              Create transfer
            </h2>
            <p className="mt-1 text-xs text-slate-600">
              Plan any hub move here: internal transfers between currently active locations
              (SGS, Suntronic, Kodiak, contract manufacturers, and the rest of the location
              list — not in-transit staging), or external inbound when a vendor or sister site
              sent stock nobody requested on the left. Lines are optional — add them now or
              after converting requests.
            </p>
            {newTransferDraft.origin_location_id && newTransferDraft.destination_location_id
            && newTransferDraft.origin_location_id !== newTransferDraft.destination_location_id ? (
              <div className="mt-2">
                <TransferLocationRoute
                  originLocation={locationsById.get(Number.parseInt(newTransferDraft.origin_location_id, 10))}
                  originLocationId={Number.parseInt(newTransferDraft.origin_location_id, 10)}
                  destinationLocation={locationsById.get(Number.parseInt(newTransferDraft.destination_location_id, 10))}
                  destinationLocationId={Number.parseInt(newTransferDraft.destination_location_id, 10)}
                />
              </div>
            ) : null}
            <form
              className="mt-3 grid gap-3"
              onSubmit={(event) => {
                event.preventDefault();
                if (!hubWritesAllowed) return;
                if (
                  !newTransferDraft.origin_location_id
                  || !newTransferDraft.destination_location_id
                  || newTransferDraft.origin_location_id === newTransferDraft.destination_location_id
                  || !newTransferDraft.ship_by_date
                ) {
                  return;
                }
                createTransferMutation.mutate(newTransferDraft);
              }}
            >
              <div className="grid gap-3 sm:grid-cols-2">
                <div>
                  <Label>Origin site</Label>
                  <select
                    className={FILTER_SELECT_CLASS}
                    value={newTransferDraft.origin_location_id}
                    onChange={(event) =>
                      setNewTransferDraft((prev) => ({ ...prev, origin_location_id: event.target.value }))
                    }
                  >
                    <option value="">Select origin…</option>
                    {(boardQuery.data?.locations ?? []).map((location) => (
                      <option key={location.id} value={String(location.id)}>
                        {buildTransferLocationLabel(location, location.id)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Destination site</Label>
                  <select
                    className={FILTER_SELECT_CLASS}
                    value={newTransferDraft.destination_location_id}
                    onChange={(event) =>
                      setNewTransferDraft((prev) => ({
                        ...prev,
                        destination_location_id: event.target.value,
                      }))
                    }
                  >
                    <option value="">Select destination…</option>
                    {(boardQuery.data?.locations ?? []).map((location) => (
                      <option key={location.id} value={String(location.id)}>
                        {buildTransferLocationLabel(location, location.id)}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label>Ship by</Label>
                  <Input
                    type="date"
                    value={newTransferDraft.ship_by_date}
                    onChange={(event) =>
                      setNewTransferDraft((prev) => ({ ...prev, ship_by_date: event.target.value }))
                    }
                  />
                </div>
                <div>
                  <TransferReasonSelect
                    label="Default reason for all lines"
                    value={newTransferDraft.default_line_reason}
                    onChange={(reason) =>
                      setNewTransferDraft((prev) => ({ ...prev, default_line_reason: reason }))
                    }
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="mt-1"
                    onClick={() =>
                      setNewTransferDraft((prev) => ({
                        ...prev,
                        lines: prev.lines.map((line) => ({
                          ...line,
                          reason: prev.default_line_reason,
                        })),
                      }))
                    }
                  >
                    Apply default to all lines
                  </Button>
                </div>
              </div>
              <div>
                <Label>Notes</Label>
                <Input
                  value={newTransferDraft.notes}
                  onChange={(event) => setNewTransferDraft((prev) => ({ ...prev, notes: event.target.value }))}
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">
                    Lines (optional)
                  </span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setNewTransferDraft((prev) => ({
                        ...prev,
                        lines: [...prev.lines, { sku: "", quantity: "", reason: prev.default_line_reason }],
                      }))
                    }
                  >
                    + Add line
                  </Button>
                </div>
                {newTransferDraft.lines.length === 0 ? (
                  <p className="mt-1 text-xs text-slate-500">
                    No lines yet — create the header now and add SKUs from requests or the table below.
                  </p>
                ) : (
                  <div className="-mx-1 mt-1 overflow-x-auto">
                  <table className="min-w-full border-collapse text-sm">
                    <thead className="text-left text-xs text-slate-500">
                      <tr>
                        <th className="px-2 py-1">SKU</th>
                        <TransferOriginOnHandHeader className="px-2 py-1 text-right" />
                        <th className="px-2 py-1 text-right">Qty</th>
                        <th className="px-2 py-1">Reason</th>
                        <th className="px-2 py-1 text-right">Remove</th>
                      </tr>
                    </thead>
                    <tbody>
                      {newTransferDraft.lines.map((line, index) => {
                        const parsedOriginId = Number.parseInt(newTransferDraft.origin_location_id, 10);
                        const lineOnHand = Number.isFinite(parsedOriginId)
                          ? lookupOriginOnHand(parsedOriginId, line.sku)
                          : { value: null, siteMissing: true };
                        const lineQty = Number.parseInt(line.quantity, 10);
                        return (
                        <tr key={index} className="border-b border-slate-100 last:border-b-0">
                          <td className="px-2 py-1">
                            <AssemblySkuCombobox
                              value={line.sku}
                              onChange={(next) =>
                                setNewTransferDraft((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((row, rowIndex) =>
                                    rowIndex === index ? { ...row, sku: next } : row,
                                  ),
                                }))
                              }
                              onBlurCommittedWithSku={(trimmed) =>
                                setNewTransferDraft((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((row, rowIndex) =>
                                    rowIndex === index ? { ...row, sku: trimmed } : row,
                                  ),
                                }))
                              }
                              enforceCatalogSku
                              placeholder="Search SKU…"
                              className="min-w-[12rem]"
                            />
                          </td>
                          <TransferOriginOnHandCell
                            value={lineOnHand.value}
                            loading={originInventoryLoading}
                            siteMissing={lineOnHand.siteMissing}
                            compareQuantity={Number.isFinite(lineQty) ? lineQty : undefined}
                            sku={line.sku}
                            originSiteCode={
                              Number.isFinite(parsedOriginId)
                                ? mrpSiteCodeForLocationId(locationsById, parsedOriginId)
                                : null
                            }
                            className="px-2 py-1"
                          />
                          <td className="px-2 py-1 text-right">
                            <Input
                              type="number"
                              min={1}
                              value={line.quantity}
                              onChange={(event) =>
                                setNewTransferDraft((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((row, rowIndex) =>
                                    rowIndex === index ? { ...row, quantity: event.target.value } : row,
                                  ),
                                }))
                              }
                              className="w-24 text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <TransferReasonSelect
                              value={line.reason}
                              onChange={(reason) =>
                                setNewTransferDraft((prev) => ({
                                  ...prev,
                                  lines: prev.lines.map((row, rowIndex) =>
                                    rowIndex === index ? { ...row, reason } : row,
                                  ),
                                }))
                              }
                              triggerClassName="min-w-[12rem]"
                            />
                          </td>
                          <td className="px-2 py-1 text-right">
                            <Button
                              type="button"
                              variant="secondary"
                              size="sm"
                              className="shrink-0 whitespace-nowrap"
                              aria-label={
                                line.sku.trim() !== ""
                                  ? `Remove line ${line.sku.trim()}`
                                  : "Remove line"
                              }
                              onClick={() =>
                                setNewTransferDraft((prev) => ({
                                  ...prev,
                                  lines: prev.lines.filter((_, rowIndex) => rowIndex !== index),
                                }))
                              }
                            >
                              Remove
                            </Button>
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                  </table>
                  </div>
                )}
              </div>
              {newTransferDraft.origin_location_id
              && newTransferDraft.destination_location_id
              && newTransferDraft.origin_location_id === newTransferDraft.destination_location_id ? (
                <p className="text-sm text-rose-700">Origin and destination must differ.</p>
              ) : null}
              {createTransferMutation.isError ? (
                <p className="text-sm text-rose-700">{createTransferMutation.error.message}</p>
              ) : null}
              <div className="flex flex-wrap justify-end gap-2 pt-2">
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={createTransferMutation.isPending}
                  onClick={() => {
                    setCreateTransferDialogOpen(false);
                    setNewTransferDraft(EMPTY_NEW_TRANSFER_DRAFT);
                    createTransferMutation.reset();
                  }}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  size="sm"
                  isLoading={createTransferMutation.isPending}
                  disabled={
                    !hubWritesAllowed
                    || !newTransferDraft.origin_location_id
                    || !newTransferDraft.destination_location_id
                    || newTransferDraft.origin_location_id === newTransferDraft.destination_location_id
                    || !newTransferDraft.ship_by_date
                  }
                >
                  Create transfer
                </Button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {/* ----- Receive dialog ----- */}
      {receiveDialog != null ? (
        <div
          {...hubModalOverlayProps()}
          role="dialog"
          aria-modal="true"
          aria-labelledby="transfer-receive-dialog-title"
        >
          <div className="max-h-[90vh] w-full max-w-6xl overflow-y-auto rounded-lg bg-white p-4 shadow-xl">
            <div className="flex items-center gap-2">
              <h2 id="transfer-receive-dialog-title" className="text-lg font-semibold text-slate-900">
                Receive {receiveDialog.transferNumber ?? `Transfer #${String(receiveDialog.transferOrderId)}`}
              </h2>
              <HoverHintWithGlyph hoverHint={RECEIVE_DIALOG_HOVER_HINT} size="md" />
            </div>
            <table className="mt-3 min-w-full border-collapse text-sm">
              <thead className="bg-slate-50 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">
                <tr>
                  <th className="border-b border-slate-200 px-2 py-2">SKU</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Expected</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Already received</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Receiving now</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Return to origin (qty)</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Lost (qty)</th>
                  <th className="border-b border-slate-200 px-2 py-2 text-right">Remaining</th>
                  <th className="border-b border-slate-200 px-2 py-2">Notes</th>
                </tr>
              </thead>
              <tbody>
                {receiveDialog.lines.map((line, index) => {
                  const remaining = transferReceiveDraftRemaining(line);
                  const patchLine = (patch: Partial<ReceiveDraftLine>) => {
                    setReceiveDialog((current) =>
                      current
                        ? {
                            ...current,
                            lines: current.lines.map((row, rowIndex) =>
                              rowIndex === index ? { ...row, ...patch } : row,
                            ),
                          }
                        : current,
                    );
                  };
                  return (
                    <tr key={line.transfer_order_item_id} className="border-b border-slate-100 last:border-b-0">
                      <td className="px-2 py-2 font-mono text-xs text-slate-900">
                        <MrpSkuWithUnitLabel
                          sku={line.sku_id ?? "—"}
                          unit={boardSkuHoverHints?.[(line.sku_id ?? "").trim()]?.mv_unit}
                        />
                      </td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700">{line.expected_qty}</td>
                      <td className="px-2 py-2 text-right tabular-nums text-slate-700">{line.already_received_qty}</td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          min={0}
                          value={line.received_qty}
                          onChange={(event) => patchLine({ received_qty: event.target.value })}
                          className="h-8 w-24 text-right"
                          aria-label={`Receiving now for ${line.sku_id ?? "line"}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          min={0}
                          value={line.return_to_origin_qty}
                          onChange={(event) => patchLine({ return_to_origin_qty: event.target.value })}
                          className="h-8 w-24 text-right"
                          aria-label={`Return to origin for ${line.sku_id ?? "line"}`}
                        />
                      </td>
                      <td className="px-2 py-2 text-right">
                        <Input
                          type="number"
                          min={0}
                          value={line.lost_qty}
                          onChange={(event) => patchLine({ lost_qty: event.target.value })}
                          className="h-8 w-24 text-right"
                          aria-label={`Lost quantity for ${line.sku_id ?? "line"}`}
                        />
                      </td>
                      <td
                        className={`px-2 py-2 text-right tabular-nums ${remaining < 0 ? "font-medium text-rose-700" : "text-slate-900"}`}
                      >
                        {remaining}
                      </td>
                      <td className="px-2 py-2">
                        <Input
                          value={line.notes}
                          onChange={(event) => patchLine({ notes: event.target.value })}
                          className="h-8 min-w-[10rem]"
                          aria-label={`Notes for ${line.sku_id ?? "line"}`}
                        />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            <div className="mt-3">
              <Label htmlFor="transfer-receive-notes">Event notes</Label>
              <TextArea
                id="transfer-receive-notes"
                value={receiveDialog.notes}
                rows={3}
                className="min-h-[4.5rem] font-sans"
                onChange={(event) =>
                  setReceiveDialog((current) => (current ? { ...current, notes: event.target.value } : current))
                }
              />
            </div>
            <label className="mt-3 flex items-center gap-2 text-sm text-slate-800">
              <input
                type="checkbox"
                checked={receiveDialog.createFollowUpTransfer}
                onChange={(event) =>
                  setReceiveDialog((current) =>
                    current ? { ...current, createFollowUpTransfer: event.target.checked } : current,
                  )
                }
              />
              Create new transfer
              <HoverHintWithGlyph
                hoverHint="When on, submit also creates a new pending transfer on the same origin and destination for the returned and lost quantities."
                size="sm"
              />
            </label>
            {receiveMutation.isError ? (
              <p
                role="alert"
                className="mt-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800"
              >
                {receiveMutation.error.message}
              </p>
            ) : null}
            <div className="mt-4 flex flex-wrap justify-end gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={receiveMutation.isPending}
                onClick={() => {
                  setReceiveDialog(null);
                  receiveMutation.reset();
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                isLoading={receiveMutation.isPending}
                onClick={() => {
                  if (!receiveDialog) return;
                  if (receiveDialog.lines.some((line) => transferReceiveDraftRemaining(line) < 0)) {
                    showToast({
                      tone: "error",
                      message: "Receiving, return, and lost cannot exceed remaining in transit.",
                    });
                    return;
                  }
                  const validLines = receiveDialog.lines.filter((line) => {
                    const receivedQty = parseReceiveDraftQty(line.received_qty);
                    const returnQty = parseReceiveDraftQty(line.return_to_origin_qty);
                    const lostQty = parseReceiveDraftQty(line.lost_qty);
                    return (
                      (Number.isInteger(receivedQty) && receivedQty > 0)
                      || (Number.isInteger(returnQty) && returnQty > 0)
                      || (Number.isInteger(lostQty) && lostQty > 0)
                    );
                  });
                  if (validLines.length === 0) {
                    showToast({
                      tone: "error",
                      message: "Enter a received, return, or lost quantity on at least one line.",
                    });
                    return;
                  }
                  receiveMutation.mutate({
                    orderId: receiveDialog.transferOrderId,
                    lines: receiveDialog.lines,
                    notes: receiveDialog.notes,
                    createFollowUpTransfer: receiveDialog.createFollowUpTransfer,
                  });
                }}
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
